import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { Link, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchEventOrders } from '../../store/slices/eventOrderSlice';
import { fetchDishes } from '../../store/slices/dishSlice';
import { fetchSemiFinished } from '../../store/slices/semiFinishedSlice';
import { fetchProducts } from '../../store/slices/productSlice';
import { fetchPreparationOperations } from '../../store/slices/preparationOperationSlice';
import SearchSelect from '../../components/SearchSelect';
import api from '../../api/api';
import { PrintSettings } from '../../types';
import { formatDishName } from '../../utils/dishNameFormat';
import { getPreparationIngredientGroupKey } from '../../utils/preparationIngredientKey';
import { formatPreparationQuantity, preparationPieceCount } from '../../utils/preparationPieces';
import { openAndPrintHtml } from '../../utils/printDocument';

type PrepSemiItem = {
  id: number | null;
  name: string;
  qtyPerPortion: number;
  piecesPerPortion?: number | null;
};

type PrepSlotColumn = {
  key: string;
  dateKey: string;
  dateLabel: string;
  venueLabel: string;
};

type PrepDishRow = {
  dishId: number;
  orderId: number;
  dishName: string;
  category: string;
  cookingTimePerPortion: number;
  serveTimePerPortion: number;
  totalPortions: number;
  portionsBySlot: Record<string, number>;
  semiItems: PrepSemiItem[];
};

type SemiDetailRow = {
  rowKey: string;
  itemType: 'product' | 'semi';
  itemName: string;
  qtyPerSemiUnit: number;
};

type AggregateDishPrintRow = {
  dishId: number;
  dishName: string;
  totalPortions: number;
  portionsByVenue: Record<string, number>;
};

type PrintLayoutSettings = {
  nameColWidth: number;
  compactColWidth: number;
  slotColWidth: number;
  cellFontSize: number;
  cellPaddingY: number;
  cellPaddingX: number;
  headerLineHeight: number;
};
type PrintLayoutDraft = Record<keyof PrintLayoutSettings, string>;
type WorksheetQuantityMode = 'brutto' | 'netto' | 'output';

const PREP_PRINT_LAYOUT_STORAGE_KEY = 'crm4chef.preparation.printLayout.v1';
const PREP_VENUE_COLORS_STORAGE_KEY = 'crm4chef.preparation.venueColors.v1';
const CONFIRMED_EVENT_ORDER_STATUS_ID = 2;
const DEFAULT_VENUE_COLORS = ['#27ae60', '#e74c3c', '#2980b9', '#8e44ad', '#e67e22', '#16a085'];
const defaultPrintLayout: PrintLayoutSettings = {
  nameColWidth: 78,
  compactColWidth: 6,
  slotColWidth: 7,
  cellFontSize: 10,
  cellPaddingY: 4,
  cellPaddingX: 5,
  headerLineHeight: 1.1,
};
const toPrintLayoutDraft = (layout: PrintLayoutSettings): PrintLayoutDraft => ({
  nameColWidth: String(layout.nameColWidth),
  compactColWidth: String(layout.compactColWidth),
  slotColWidth: String(layout.slotColWidth),
  cellFontSize: String(layout.cellFontSize),
  cellPaddingY: String(layout.cellPaddingY),
  cellPaddingX: String(layout.cellPaddingX),
  headerLineHeight: String(layout.headerLineHeight),
});

const toDateKey = (value: string) => {
  if (!value) return '';
  return value.length >= 10 ? value.slice(0, 10) : value;
};

const formatDateLabel = (dateKey: string) => {
  const date = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateKey;
  return date.toLocaleDateString('uk-UA');
};

const normalizeVenueLabel = (value?: string | null) => {
  const normalized = value?.trim() || '';
  return normalized || 'Без вказаного місця';
};

const getDefaultVenueColor = (venue: string, index: number) => {
  const normalized = venue.toLocaleLowerCase('uk');
  if (/весільний\s*№?\s*1\b/.test(normalized)) return '#27ae60';
  if (/весільний\s*№?\s*2\b/.test(normalized)) return '#e74c3c';
  return DEFAULT_VENUE_COLORS[index % DEFAULT_VENUE_COLORS.length];
};

const isValidVenueColor = (value: unknown): value is string =>
  typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value);

const compactVenuePrintLabel = (value: string) => value.replace(/Весільний/gi, 'Вес:');
const formatDurationLabel = (seconds: number) => {
  const safeSeconds = Math.max(0, Math.round(seconds));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const restSeconds = safeSeconds % 60;
  if (hours > 0) return `${hours} год ${minutes} хв`;
  if (minutes > 0) return `${minutes} хв ${restSeconds} сек`;
  return `${restSeconds} сек`;
};
const calcRequiredTime = (secondsPerPortion: number, portions: number) => {
  const safePerPortion = Number.isFinite(secondsPerPortion) ? Math.max(0, secondsPerPortion) : 0;
  const safePortions = Number.isFinite(portions) ? Math.max(0, portions) : 0;
  return safePerPortion * safePortions;
};

const getPrintDishKey = (dishId: number) => String(dishId);
const getPrintItemKey = (dishId: number, semiName: string) => `${dishId}::${semiName}`;

const PreparationWorksheetsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { orders, loading } = useAppSelector((s) => s.eventOrder);
  const dishes = useAppSelector((s) => s.dish.items);
  const semiFinished = useAppSelector((s) => s.semiFinished.items);
  const products = useAppSelector((s) => s.product.products);
  const preparationOperations = useAppSelector((s) => s.preparationOperation.items);
  const [printSettings, setPrintSettings] = useState<PrintSettings | null>(null);
  const [customVenueColors, setCustomVenueColors] = useState<Record<string, string>>(() => {
    try {
      const saved = localStorage.getItem(PREP_VENUE_COLORS_STORAGE_KEY);
      if (!saved) return {};
      const parsed = JSON.parse(saved) as Record<string, unknown>;
      return Object.fromEntries(Object.entries(parsed).filter((entry): entry is [string, string] => isValidVenueColor(entry[1])));
    } catch {
      return {};
    }
  });
  const [searchParams] = useSearchParams();
  const formatPrepDishName = useCallback((dishId: number, dishName: string) => {
    const dish = dishes.find((item) => item.id === dishId);
    return formatDishName(
      {
        name: dishName,
        output: dish?.cleanOutput,
        servingsCount: dish?.servingsCount,
      },
      printSettings
    );
  }, [dishes, printSettings]);

  const [selectedOrderIds, setSelectedOrderIds] = useState<number[]>([]);
  const [rows, setRows] = useState<PrepDishRow[]>([]);
  const [slotColumns, setSlotColumns] = useState<PrepSlotColumn[]>([]);
  const [printDishSelection, setPrintDishSelection] = useState<Record<string, boolean>>({});
  const [printItemSelection, setPrintItemSelection] = useState<Record<string, boolean>>({});
  const [printSemiListSelection, setPrintSemiListSelection] = useState<Record<string, boolean>>({});
  const [selectedSemiKeys, setSelectedSemiKeys] = useState<string[]>([]);
  const [quickSemiName, setQuickSemiName] = useState('');
  const [quickSemiQuery, setQuickSemiQuery] = useState('');
  const [quickSemiDropdownOpen, setQuickSemiDropdownOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'dishes' | 'semi'>('dishes');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [dishFilter, setDishFilter] = useState('');
  const [dishSearch, setDishSearch] = useState('');
  const [semiSearch, setSemiSearch] = useState('');
  const [printLayout, setPrintLayout] = useState<PrintLayoutSettings>({ ...defaultPrintLayout });
  const [printLayoutDraft, setPrintLayoutDraft] = useState<PrintLayoutDraft>(toPrintLayoutDraft(defaultPrintLayout));
  const [sortBy, setSortBy] = useState<'dish-asc' | 'dish-desc' | 'category-asc' | 'category-desc' | 'portions-asc' | 'portions-desc'>('dish-asc');
  const [worksheetQuantityMode, setWorksheetQuantityMode] = useState<WorksheetQuantityMode>('netto');
  const [includeSemiDetailsInDishPrint, setIncludeSemiDetailsInDishPrint] = useState(false);
  const [expandedDishSemiRows, setExpandedDishSemiRows] = useState<Record<string, boolean>>({});
  const [printOperationNotes, setPrintOperationNotes] = useState(true);
  const [printCardNotes, setPrintCardNotes] = useState(false);
  const quickSemiSearchRef = useRef<HTMLDivElement>(null);
  const resolveItemQty = useCallback((item: { nettoWeight?: number | null; bruttoWeight?: number | null; output?: number | null; quantity?: number | null }) => {
    const netto = Number(item.nettoWeight ?? 0);
    const brutto = Number(item.bruttoWeight ?? 0);
    const output = Number(item.output ?? 0);
    const quantity = Number(item.quantity ?? 0);

    const safeNetto = Number.isFinite(netto) && netto > 0 ? netto : 0;
    const safeBrutto = Number.isFinite(brutto) && brutto > 0 ? brutto : 0;
    const safeOutput = Number.isFinite(output) && output > 0 ? output : 0;
    const safeQuantity = Number.isFinite(quantity) && quantity > 0 ? quantity : 0;

    if (worksheetQuantityMode === 'netto') {
      if (safeNetto > 0) return safeNetto;
      if (safeBrutto > 0) return safeBrutto;
      if (safeOutput > 0) return safeOutput;
      return safeQuantity;
    }

    if (worksheetQuantityMode === 'brutto') {
      if (safeBrutto > 0) return safeBrutto;
      if (safeNetto > 0) return safeNetto;
      if (safeOutput > 0) return safeOutput;
      return safeQuantity;
    }

    if (safeOutput > 0) return safeOutput;
    if (safeNetto > 0) return safeNetto;
    if (safeBrutto > 0) return safeBrutto;
    return safeQuantity;
  }, [worksheetQuantityMode]);

  useEffect(() => {
    dispatch(fetchEventOrders());
    dispatch(fetchDishes());
    dispatch(fetchSemiFinished());
    dispatch(fetchProducts());
    dispatch(fetchPreparationOperations());
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;
    api.get<PrintSettings>('/permissions/print-settings')
      .then((response) => { if (!cancelled) setPrintSettings(response.data); })
      .catch(() => { if (!cancelled) setPrintSettings(null); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(PREP_PRINT_LAYOUT_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Partial<PrintLayoutSettings>;
      const keys = Object.keys(defaultPrintLayout) as Array<keyof PrintLayoutSettings>;
      const restored: PrintLayoutSettings = { ...defaultPrintLayout };
      keys.forEach((key) => {
        const value = parsed[key];
        if (typeof value === 'number' && Number.isFinite(value)) restored[key] = value;
      });
      setPrintLayout(restored);
      setPrintLayoutDraft(toPrintLayoutDraft(restored));
    } catch {
      // ignore invalid localStorage payload
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(PREP_VENUE_COLORS_STORAGE_KEY, JSON.stringify(customVenueColors));
  }, [customVenueColors]);

  useEffect(() => {
    const orderIdParam = parseInt(searchParams.get('orderId') || '', 10);
    if (Number.isFinite(orderIdParam) && orderIdParam > 0) {
      setSelectedOrderIds((prev) => (prev.includes(orderIdParam) ? prev : [...prev, orderIdParam]));
    }
  }, [searchParams]);

  const semiById = useMemo(() => new Map(semiFinished.map((s) => [s.id, s])), [semiFinished]);
  const dishById = useMemo(() => new Map(dishes.map((d) => [d.id, d])), [dishes]);
  const productCategoryByName = useMemo(() => {
    const map = new Map<string, string>();
    products.forEach((product) => {
      const key = (product.name || '').trim().toLowerCase();
      if (!key || map.has(key)) return;
      map.set(key, product.categoryName?.trim() || 'Без категорії');
    });
    return map;
  }, [products]);

  const isConfirmedOrder = useCallback(
    (order?: { orderStatusId?: number; orderStatusCode?: string | null } | null) =>
      order?.orderStatusId === CONFIRMED_EVENT_ORDER_STATUS_ID || (order?.orderStatusCode || '').trim().toLowerCase() === 'confirmed',
    []
  );
  const selectedOrders = useMemo(
    () => orders.filter((o) => selectedOrderIds.includes(o.id) && isConfirmedOrder(o)),
    [orders, selectedOrderIds, isConfirmedOrder]
  );
  const confirmedOrders = useMemo(
    () => orders.filter((o) => isConfirmedOrder(o)),
    [orders, isConfirmedOrder]
  );

  useEffect(() => {
    setSelectedOrderIds((prev) => prev.filter((id) => confirmedOrders.some((o) => o.id === id)));
  }, [confirmedOrders]);

  const toggleOrder = (orderId: number) => {
    const order = orders.find((o) => o.id === orderId);
    if (!order || !isConfirmedOrder(order)) {
      window.alert('Для бланка доступні лише замовлення зі статусом "Підтверджене".');
      return;
    }
    setSelectedOrderIds((prev) => {
      if (prev.includes(orderId)) return prev.filter((id) => id !== orderId);
      return [...prev, orderId];
    });
  };
  const selectAllOrders = () => {
    setSelectedOrderIds(confirmedOrders.map((o) => o.id));
  };
  const clearAllOrdersSelection = () => setSelectedOrderIds([]);

  const setPrintLayoutDraftValue = (key: keyof PrintLayoutSettings, value: string) => {
    setPrintLayoutDraft((prev) => ({ ...prev, [key]: value }));
  };
  const savePrintLayoutPreset = () => {
    const limits: Record<keyof PrintLayoutSettings, [number, number]> = {
      nameColWidth: [10, 90],
      compactColWidth: [2, 20],
      slotColWidth: [2, 20],
      cellFontSize: [9, 14],
      cellPaddingY: [2, 12],
      cellPaddingX: [2, 14],
      headerLineHeight: [1, 1.6],
    };

    const next: PrintLayoutSettings = { ...printLayout };
    (Object.keys(limits) as Array<keyof PrintLayoutSettings>).forEach((key) => {
      const [min, max] = limits[key];
      const parsed = parseFloat((printLayoutDraft[key] || '').replace(',', '.'));
      if (!Number.isFinite(parsed)) return;
      next[key] = Math.min(max, Math.max(min, parsed));
    });

    setPrintLayout(next);
    setPrintLayoutDraft(toPrintLayoutDraft(next));
    try {
      localStorage.setItem(PREP_PRINT_LAYOUT_STORAGE_KEY, JSON.stringify(next));
    } catch {
      // ignore storage errors
    }
  };

  const printTableLayoutCss = `
    .col-name{width:${printLayout.nameColWidth}%;white-space:normal;word-break:break-word;overflow-wrap:anywhere;}
    .col-compact{width:${printLayout.compactColWidth}%;padding-left:2px;padding-right:2px;}
    .col-slot, table.print-table th:not(.col-name):not(.col-compact), table.print-table td:not(.col-name):not(.col-compact){width:${printLayout.slotColWidth}%;padding-left:2px;padding-right:2px;font-size:${Math.max(printLayout.cellFontSize - 0.5, 9)}px;}
    th,td{border:1px solid #d1d5db;padding:${printLayout.cellPaddingY}px ${printLayout.cellPaddingX}px;font-size:${printLayout.cellFontSize}px;vertical-align:top;}
    th{background:#f3f4f6;text-align:left;white-space:normal;word-break:break-word;overflow-wrap:anywhere;line-height:${printLayout.headerLineHeight};}
    td.col-slot, td.col-compact, table.print-table td:not(.col-name):not(.col-compact){white-space:nowrap;}
  `;

  const buildSemiBreakdownForDish = useCallback((dishId: number): PrepSemiItem[] => {
    const dish = dishById.get(dishId);
    if (!dish) return [];

    const totals = new Map<string, { id: number | null; name: string; qty: number; pieces: number | null }>();
    const addSemi = (id: number | null, name: string, qty: number, pieces: number | null) => {
      if (!name || qty <= 0) return;
      const key = `${id ? `id:${id}` : `name:${name}`}|${pieces != null ? 'pieces' : 'weight'}`;
      const current = totals.get(key);
      totals.set(key, {
        id,
        name,
        qty: (current?.qty || 0) + qty,
        pieces: pieces != null ? (current?.pieces ?? 0) + pieces : null,
      });
    };

    const expandSemi = (semiId: number, qty: number, pieces: number | null, fallbackName?: string | null) => {
      const semi = semiById.get(semiId);
      if (semi) {
        addSemi(semi.id, semi.name, qty, pieces);
        return;
      }
      const normalizedFallback = (fallbackName || '').trim();
      if (normalizedFallback) {
        addSemi(null, normalizedFallback, qty, pieces);
      }
    };

    dish.ingredients.forEach((ingredient) => {
      const qty = resolveItemQty(ingredient);
      const pieces = preparationPieceCount(ingredient.piecesPerPortion, 1);
      if (qty <= 0) return;
      if (ingredient.semiFinishedProductId) {
        expandSemi(ingredient.semiFinishedProductId, qty, pieces, ingredient.semiFinishedProductName);
      } else if (ingredient.productId && ingredient.productName) {
        addSemi(null, ingredient.productName, qty, pieces);
      }
    });

    return Array.from(totals.values())
      .map((entry) => ({ id: entry.id, name: entry.name, qtyPerPortion: entry.qty, piecesPerPortion: entry.pieces }))
      .sort((a, b) => a.name.localeCompare(b.name, 'uk'));
  }, [dishById, semiById, resolveItemQty]);

  const generateWorksheet = () => {
    if (selectedOrders.length === 0) {
      window.alert('Оберіть хоча б одне замовлення.');
      return;
    }

    const slotMeta = new Map<string, { dateKey: string; dateLabel: string; venueLabel: string }>();
    const portionsByDish = new Map<number, Map<string, number>>();
    const dishNamesById = new Map<number, string>();
    const addDishPortions = (dishId: number, slotKey: string, portions: number, dishName?: string) => {
      if (!dishId || portions <= 0) return;
      if (!slotKey) return;
      if (dishName?.trim() && !dishNamesById.has(dishId)) {
        dishNamesById.set(dishId, dishName.trim());
      }
      if (!portionsByDish.has(dishId)) {
        portionsByDish.set(dishId, new Map<string, number>());
      }
      const dishSlotMap = portionsByDish.get(dishId)!;
      dishSlotMap.set(slotKey, (dishSlotMap.get(slotKey) || 0) + portions);
    };

    selectedOrders.forEach((order) => {
      const dateKey = toDateKey(order.eventDate);
      if (!dateKey) return;
      const venueLabel = normalizeVenueLabel(order.eventVenue);
      const slotKey = `${dateKey}__${venueLabel}`;
      if (!slotMeta.has(slotKey)) {
        slotMeta.set(slotKey, {
          dateKey,
          dateLabel: formatDateLabel(dateKey),
          venueLabel,
        });
      }

      (order.dishes || []).forEach((d) => addDishPortions(d.dishId, slotKey, d.portions || 0, d.dishName));
      (order.children || []).forEach((d) => addDishPortions(d.dishId, slotKey, d.portions || 0, d.dishName));
      (order.contractors || []).forEach((d) => addDishPortions(d.dishId, slotKey, d.portions || 0, d.dishName));
      (order.customSections || []).forEach((section) => {
        (section.dishes || []).forEach((d) => addDishPortions(d.dishId, slotKey, d.portions || 0, d.dishName));
      });
    });

    const nextSlotColumns: PrepSlotColumn[] = Array.from(slotMeta.entries())
      .map(([key, value]) => ({ key, ...value }))
      .sort((a, b) => {
        const byDate = a.dateKey.localeCompare(b.dateKey);
        if (byDate !== 0) return byDate;
        return a.venueLabel.localeCompare(b.venueLabel, 'uk');
      });

    const generatedRows: PrepDishRow[] = Array.from(portionsByDish.entries()).map(([dishId, dishSlotMap]) => {
      const dish = dishById.get(dishId);
      const portionsBySlot: Record<string, number> = {};
      let totalPortions = 0;
      dishSlotMap.forEach((portions, slotKey) => {
        portionsBySlot[slotKey] = portions;
        totalPortions += portions;
      });

      // Find the first order that contains this dish
      const orderId = selectedOrders.find((order) => {
        const allDishes = [
          ...(order.dishes || []),
          ...(order.children || []),
          ...(order.contractors || []),
          ...(order.customSections || []).flatMap((s) => s.dishes || []),
        ];
        return allDishes.some((d) => d.dishId === dishId);
      })?.id || 0;

      return {
        dishId,
        orderId,
        dishName: dish?.name || dishNamesById.get(dishId) || `Страва #${dishId}`,
        category: dish?.categoryName || 'Без категорії',
        cookingTimePerPortion: Math.max(0, Number(dish?.cookingTime ?? 0)),
        serveTimePerPortion: Math.max(0, Number(dish?.serveTime ?? 0)),
        totalPortions,
        portionsBySlot,
        semiItems: buildSemiBreakdownForDish(dishId),
      };
    });
    const initialPrintSelection: Record<string, boolean> = {};
    const initialPrintItems: Record<string, boolean> = {};
    generatedRows.forEach((row) => {
      initialPrintSelection[getPrintDishKey(row.dishId)] = true;
      row.semiItems.forEach((semi) => {
        initialPrintItems[getPrintItemKey(row.dishId, semi.name)] = true;
      });
    });

    setSlotColumns(nextSlotColumns);
    setRows(generatedRows);
    setPrintDishSelection(initialPrintSelection);
    setPrintItemSelection(initialPrintItems);
    setSelectedSemiKeys([]);
    setQuickSemiName('');
    setActiveTab('dishes');
    setCategoryFilter('');
    setDishFilter('');
    setDishSearch('');
    setSortBy('dish-asc');
    setExpandedDishSemiRows({});
  };

  useEffect(() => {
    setRows((prev) => {
      if (prev.length === 0) return prev;
      return prev.map((row) => ({
        ...row,
        semiItems: buildSemiBreakdownForDish(row.dishId),
      }));
    });
  }, [worksheetQuantityMode, buildSemiBreakdownForDish]);
  const categoryOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.category).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'uk')),
    [rows]
  );
  const dishOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.dishName).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'uk')),
    [rows]
  );
  const categorySearchItems = useMemo(
    () => categoryOptions.map((category) => ({ id: category, name: category })),
    [categoryOptions]
  );
  const dishSearchItems = useMemo(
    () => dishOptions.map((dishName) => ({ id: dishName, name: dishName })),
    [dishOptions]
  );

  const filteredRows = useMemo(() => {
    const dishSearchQuery = dishSearch.trim().toLowerCase();
    const filtered = rows.filter((row) => {
      if (categoryFilter && row.category !== categoryFilter) return false;
      if (dishFilter && row.dishName !== dishFilter) return false;
      if (dishSearchQuery && !row.dishName.toLowerCase().includes(dishSearchQuery)) return false;
      return true;
    });
    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'dish-desc': return b.dishName.localeCompare(a.dishName, 'uk');
        case 'category-asc': return a.category.localeCompare(b.category, 'uk');
        case 'category-desc': return b.category.localeCompare(a.category, 'uk');
        case 'portions-asc': return a.totalPortions - b.totalPortions;
        case 'portions-desc': return b.totalPortions - a.totalPortions;
        case 'dish-asc':
        default: return a.dishName.localeCompare(b.dishName, 'uk');
      }
    });
  }, [rows, categoryFilter, dishFilter, dishSearch, sortBy]);

  const isDishSelectedForPrint = (dishId: number) =>
    printDishSelection[getPrintDishKey(dishId)] !== false;

  const togglePrintDish = (dishId: number) => {
    const key = getPrintDishKey(dishId);
    setPrintDishSelection((prev) => ({ ...prev, [key]: !(prev[key] !== false) }));
  };

  const setFilteredDishPrintSelection = (selected: boolean) => {
    setPrintDishSelection((prev) => {
      const next = { ...prev };
      filteredRows.forEach((row) => {
        next[getPrintDishKey(row.dishId)] = selected;
      });
      return next;
    });
  };

  const isPrintItemSelected = (dishId: number, semiName: string) =>
    printItemSelection[getPrintItemKey(dishId, semiName)] !== false;

  const togglePrintItem = (dishId: number, semiName: string) => {
    const key = getPrintItemKey(dishId, semiName);
    setPrintItemSelection((prev) => ({ ...prev, [key]: !(prev[key] !== false) }));
  };

  const getDishSemiExpandKey = (dishId: number, semiId: number) => `${dishId}::${semiId}`;
  const isDishSemiExpanded = (dishId: number, semiId: number) =>
    expandedDishSemiRows[getDishSemiExpandKey(dishId, semiId)] === true;
  const toggleDishSemiExpanded = (dishId: number, semiId: number) => {
    const key = getDishSemiExpandKey(dishId, semiId);
    setExpandedDishSemiRows((prev) => ({ ...prev, [key]: !prev[key] }));
  };
const availableSemiNames = useMemo(
    () => Array.from(new Set(filteredRows.flatMap((row) => row.semiItems.map((semi) => semi.name)))).sort((a, b) => a.localeCompare(b, 'uk')),
    [filteredRows]
  );
  const filteredQuickSemiNames = useMemo(() => {
    const query = quickSemiQuery.trim().toLowerCase();
    if (!query) return availableSemiNames;
    return availableSemiNames.filter((name) => name.toLowerCase().includes(query));
  }, [availableSemiNames, quickSemiQuery]);

  useEffect(() => {
    if (availableSemiNames.length === 0) {
      if (quickSemiName) setQuickSemiName('');
      if (quickSemiQuery) setQuickSemiQuery('');
      return;
    }
    if (quickSemiName && !availableSemiNames.includes(quickSemiName)) {
      setQuickSemiName('');
    }
    if (!quickSemiQuery) {
      const fallback = availableSemiNames[0];
      setQuickSemiName((prev) => prev || fallback);
      setQuickSemiQuery((prev) => prev || fallback);
    }
  }, [availableSemiNames, quickSemiName, quickSemiQuery]);

  useEffect(() => {
    if (!quickSemiDropdownOpen) return;
    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (quickSemiSearchRef.current && !quickSemiSearchRef.current.contains(target)) {
        setQuickSemiDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [quickSemiDropdownOpen]);

  const setSemiNameSelectionAcrossDishes = (semiName: string, selected: boolean) => {
    if (!semiName) return;
    setPrintItemSelection((prev) => {
      const next = { ...prev };
      filteredRows.forEach((row) => {
        row.semiItems
          .filter((semi) => semi.name === semiName)
          .forEach((semi) => {
            next[getPrintItemKey(row.dishId, semi.name)] = selected;
          });
      });
      return next;
    });
  };

  const totalPrintableDishes = filteredRows.length;
  const selectedPrintableDishes = filteredRows.filter((row) => isDishSelectedForPrint(row.dishId)).length;
  const totalPrintableItems = filteredRows.reduce((sum, row) => sum + row.semiItems.length, 0);
  const selectedPrintableItems = filteredRows.reduce(
    (sum, row) => sum + row.semiItems.filter((semi) => isPrintItemSelected(row.dishId, semi.name)).length,
    0
  );
  const totalPreparationTimeSeconds = filteredRows.reduce(
    (sum, row) => sum + calcRequiredTime(row.cookingTimePerPortion, row.totalPortions),
    0
  );
  const totalServeTimeSeconds = filteredRows.reduce(
    (sum, row) => sum + calcRequiredTime(row.serveTimePerPortion, row.totalPortions),
    0
  );

  const aggregateDishRows = useMemo(() => {
    const rowsByDish = new Map<number, AggregateDishPrintRow>();
    filteredRows.forEach((row) => {
      const current = rowsByDish.get(row.dishId) ?? {
        dishId: row.dishId,
        dishName: formatPrepDishName(row.dishId, row.dishName),
        totalPortions: 0,
        portionsByVenue: {},
      };
      current.totalPortions += row.totalPortions;
      slotColumns.forEach((column) => {
        const qty = row.portionsBySlot[column.key] || 0;
        if (qty <= 0) return;
        current.portionsByVenue[column.key] = (current.portionsByVenue[column.key] || 0) + qty;
      });
      rowsByDish.set(row.dishId, current);
    });

    return Array.from(rowsByDish.values()).sort((a, b) => a.dishName.localeCompare(b.dishName, 'uk'));
  }, [filteredRows, formatPrepDishName, slotColumns]);

  const aggregateDishRowsByCategory = useMemo(() => {
    const categories = new Map<string, AggregateDishPrintRow[]>();
    aggregateDishRows.forEach((row) => {
      const sourceRow = filteredRows.find((item) => item.dishId === row.dishId);
      const categoryName = sourceRow?.category?.trim() || 'Без категорії';
      const bucket = categories.get(categoryName) ?? [];
      bucket.push(row);
      categories.set(categoryName, bucket);
    });
    return Array.from(categories.entries())
      .sort((a, b) => a[0].localeCompare(b[0], 'uk'))
      .map(([categoryName, rows]) => ({
        categoryName,
        rows: rows.sort((a, b) => a.dishName.localeCompare(b.dishName, 'uk')),
      }));
  }, [aggregateDishRows, filteredRows]);

  const aggregateTotalPortions = aggregateDishRows.reduce((sum, row) => sum + row.totalPortions, 0);
  const aggregateVenueColors = useMemo(() => {
    const map = new Map<string, string>();
    const venues = Array.from(new Set(slotColumns.map((column) => column.venueLabel))).sort((a, b) => a.localeCompare(b, 'uk'));
    venues.forEach((venue, index) => {
      map.set(venue, customVenueColors[venue] || getDefaultVenueColor(venue, index));
    });
    return map;
  }, [customVenueColors, slotColumns]);

  type SemiSummaryRow = {
    key: string;
    semiId: number | null;
    semiName: string;
    categoryName: string;
    totalQty: number;
    qtyBySlot: Record<string, number>;
  };

  const buildSemiDetailRows = useCallback((semiId: number | null): SemiDetailRow[] => {
    if (!semiId) return [];
    const aggregate = new Map<string, SemiDetailRow>();
    const addRow = (rowKey: string, itemType: 'product' | 'semi', itemName: string, qty: number) => {
      if (!itemName || qty <= 0) return;
      const current = aggregate.get(rowKey);
      aggregate.set(rowKey, {
        rowKey,
        itemType,
        itemName,
        qtyPerSemiUnit: (current?.qtyPerSemiUnit || 0) + qty,
      });
    };

    const semi = semiById.get(semiId);
    if (!semi) return [];

    semi.items.forEach((item) => {
      const itemQtyPerUnit = resolveItemQty(item);
      if (itemQtyPerUnit <= 0) return;
      if (item.productId && item.productName) {
        addRow(`product:${item.productId}`, 'product', item.productName, itemQtyPerUnit);
        return;
      }
      if (item.subSemiFinishedProductId) {
        const subSemi = semiById.get(item.subSemiFinishedProductId);
        addRow(
          `semi:${item.subSemiFinishedProductId}`,
          'semi',
          subSemi?.name || `Н/Ф #${item.subSemiFinishedProductId}`,
          itemQtyPerUnit
        );
      }
    });

    return Array.from(aggregate.values()).sort((a, b) => a.itemName.localeCompare(b.itemName, 'uk'));
  }, [semiById, resolveItemQty]);

  const buildDishSemiSummaryForPrint = useCallback((row: PrepDishRow, semi: PrepSemiItem) => {
    const qtyBySlot: Record<string, number> = {};
    slotColumns.forEach((column) => {
      const portions = row.portionsBySlot[column.key] || 0;
      const totalQty = semi.qtyPerPortion * portions;
      qtyBySlot[column.key] = totalQty > 0 ? totalQty : 0;
    });
    const totalQty = semi.qtyPerPortion * row.totalPortions;
    return {
      totalQty,
      qtyBySlot,
    };
  }, [slotColumns]);

  const printSingleSemiFromDish = useCallback((row: PrepDishRow, semi: PrepSemiItem) => {
    if (semi.id == null) return;
    const detailRows = buildSemiDetailRows(semi.id);
    const summary = buildDishSemiSummaryForPrint(row, semi);

    const escapeHtml = (value: string) =>
      value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    const rowsHtml = detailRows.map((item) => `<tr>
      <td class="col-name">${escapeHtml(`${item.itemType === 'semi' ? 'Н/Ф: ' : ''}${item.itemName}`)}</td>
      <td class="col-compact" style="text-align:right;">${item.qtyPerSemiUnit.toFixed(4)}</td>
      <td class="col-compact" style="text-align:right;font-weight:700;color:#16a085;">${(summary.totalQty * item.qtyPerSemiUnit).toFixed(4)}</td>
      ${slotColumns.map((column) => {
        const semiQty = summary.qtyBySlot[column.key] || 0;
        return `<td class="col-slot">${(semiQty * item.qtyPerSemiUnit).toFixed(4)}</td>`;
      }).join('')}
    </tr>`).join('');

    const html = `<!DOCTYPE html><html lang="uk"><head><meta charset="utf-8"/>
      <title>Калькуляція Н/Ф</title>
      <style>
        body{font-family:Arial,sans-serif;padding:20px;color:#1f2937;}
        h1,h2{margin:0 0 8px;}
        .meta{font-size:12px;color:#4b5563;margin-bottom:6px;}
        table.print-table{width:100%;border-collapse:collapse;margin-top:8px;table-layout:fixed;}
        ${printTableLayoutCss}
      </style></head><body>
      <h1>Калькуляція Н/Ф у страві</h1>
      <div class="meta">Страва: ${escapeHtml(formatPrepDishName(row.dishId, row.dishName))}</div>
      <div class="meta">Н/Ф: ${escapeHtml(semi.name)}</div>
      <div class="meta">Порцій по страві: ${row.totalPortions.toFixed(2)}</div>
      <div class="meta">Всього потрібно Н/Ф: ${summary.totalQty.toFixed(4)}</div>
      <table class="print-table">
        <thead>
          <tr>
            <th class="col-name">Позиція</th>
            <th class="col-compact">На 1 од. н/ф</th>
            <th class="col-compact" style="color:#16a085;font-weight:700;">Загалом</th>
            ${slotColumns.map((column) => `<th>${escapeHtml(column.dateLabel)}<br/><span style="font-weight:600">${escapeHtml(compactVenuePrintLabel(column.venueLabel))}</span></th>`).join('')}
          </tr>
        </thead>
        <tbody>${rowsHtml || `<tr><td colspan="${3 + slotColumns.length}">Немає розкладки для цього н/ф</td></tr>`}</tbody>
      </table>
      </body></html>`;

    void openAndPrintHtml(html, { windowFeatures: 'width=1200,height=800', minimumDelayMs: 300 });
  }, [buildDishSemiSummaryForPrint, buildSemiDetailRows, formatPrepDishName, printTableLayoutCss, slotColumns]);

  const semiOnlySummary = useMemo(() => {
    const totals = new Map<string, SemiSummaryRow>();
    const addSemiQty = (semiId: number, semiName: string, qty: number, slotKey: string) => {
      if (!semiName || qty <= 0) return;
      const key = `id:${semiId}`;
      if (!totals.has(key)) {
        const categoryName = semiById.get(semiId)?.categoryName?.trim() || 'Без категорії';
        totals.set(key, {
          key,
          semiId,
          semiName,
          categoryName,
          totalQty: 0,
          qtyBySlot: {},
        });
      }
      const entry = totals.get(key)!;
      entry.totalQty += qty;
      entry.qtyBySlot[slotKey] = (entry.qtyBySlot[slotKey] || 0) + qty;
    };

    const expandNestedSemiRequirements = (semiId: number, requiredQty: number, slotKey: string, path: Set<number>) => {
      if (requiredQty <= 0 || path.has(semiId)) return;
      const semi = semiById.get(semiId);
      if (!semi) return;

      const nextPath = new Set(path);
      nextPath.add(semiId);

      semi.items.forEach((item) => {
        const qtyPerSemiUnit = resolveItemQty(item);
        if (qtyPerSemiUnit <= 0 || !item.subSemiFinishedProductId) return;
        const nestedId = item.subSemiFinishedProductId;
        const nestedQty = requiredQty * qtyPerSemiUnit;
        const nestedSemiName = (semiById.get(nestedId)?.name || item.subSemiFinishedProductName || `Н/Ф #${nestedId}`).trim();
        addSemiQty(nestedId, nestedSemiName, nestedQty, slotKey);
        expandNestedSemiRequirements(nestedId, nestedQty, slotKey, nextPath);
      });
    };

    rows.forEach((row) => {
      row.semiItems.forEach((semi) => {
        const semiId = semi.id;
        if (semiId == null) return;
        slotColumns.forEach((column) => {
          const slotPortions = row.portionsBySlot[column.key] || 0;
          if (slotPortions <= 0) return;
          const requiredQty = semi.qtyPerPortion * slotPortions;
          if (requiredQty <= 0) return;
          const rootSemiName = (semiById.get(semiId)?.name || semi.name || `Н/Ф #${semiId}`).trim();
          addSemiQty(semiId, rootSemiName, requiredQty, column.key);
          expandNestedSemiRequirements(semiId, requiredQty, column.key, new Set<number>());
        });
      });
    });
    return Array.from(totals.values()).sort((a, b) => a.semiName.localeCompare(b.semiName, 'uk'));
  }, [rows, slotColumns, semiById, resolveItemQty]);

  const productSummary = useMemo(() => {
    const totals = new Map<string, SemiSummaryRow>();
    const addQty = (key: string, productName: string, categoryName: string, qty: number, slotKey?: string) => {
      if (qty <= 0) return;
      if (!totals.has(key)) {
        totals.set(key, {
          key,
          semiId: null,
          semiName: productName,
          categoryName,
          totalQty: 0,
          qtyBySlot: {},
        });
      }
      const entry = totals.get(key)!;
      entry.totalQty += qty;
      if (slotKey) {
        entry.qtyBySlot[slotKey] = (entry.qtyBySlot[slotKey] || 0) + qty;
      }
    };

    const collectProductsFromSemi = (
      semiId: number,
      requiredQty: number,
      slotKey: string,
      path: Set<number>
    ) => {
      if (requiredQty <= 0 || path.has(semiId)) return;
      const semi = semiById.get(semiId);
      if (!semi) return;

      const nextPath = new Set(path);
      nextPath.add(semiId);

      semi.items.forEach((item) => {
        const itemQty = resolveItemQty(item);
        if (itemQty <= 0) return;

        if (item.productId && item.productName) {
          const productKey = `product-id:${item.productId}`;
          const productCategory = productCategoryByName.get(item.productName.trim().toLowerCase()) || 'Без категорії';
          addQty(productKey, item.productName, productCategory, itemQty * requiredQty, slotKey);
          return;
        }

        if (item.subSemiFinishedProductId) {
          collectProductsFromSemi(item.subSemiFinishedProductId, requiredQty * itemQty, slotKey, nextPath);
        }
      });
    };

    rows.forEach((row) => {
      row.semiItems.forEach((item) => {
        if (item.id != null) {
          slotColumns.forEach((column) => {
            const slotPortions = row.portionsBySlot[column.key] || 0;
            if (slotPortions <= 0) return;
            collectProductsFromSemi(item.id!, item.qtyPerPortion * slotPortions, column.key, new Set<number>());
          });
          return;
        }

        if (!item.name) return;
        const productKey = `product-name:${item.name.trim().toLowerCase()}`;
        const productCategory = productCategoryByName.get(item.name.trim().toLowerCase()) || 'Без категорії';
        slotColumns.forEach((column) => {
          const slotPortions = row.portionsBySlot[column.key] || 0;
          if (slotPortions <= 0) return;
          addQty(productKey, item.name, productCategory, item.qtyPerPortion * slotPortions, column.key);
        });
      });
    });

    return Array.from(totals.values()).sort((a, b) => a.semiName.localeCompare(b.semiName, 'uk'));
  }, [rows, slotColumns, semiById, resolveItemQty, productCategoryByName]);

  const semiSummary = useMemo(
    () => [...semiOnlySummary, ...productSummary].sort((a, b) => a.semiName.localeCompare(b.semiName, 'uk')),
    [semiOnlySummary, productSummary]
  );

  const filteredSemiSummary = useMemo(() => {
    const query = semiSearch.trim().toLowerCase();
    if (!query) return semiSummary;
    return semiSummary.filter((semi) => semi.semiName.toLowerCase().includes(query));
  }, [semiSummary, semiSearch]);

  useEffect(() => {
    if (semiOnlySummary.length === 0) {
      if (selectedSemiKeys.length > 0) setSelectedSemiKeys([]);
      return;
    }
    const validKeys = selectedSemiKeys.filter((key) => semiOnlySummary.some((semi) => semi.key === key));
    if (validKeys.length > 0) {
      if (validKeys.length !== selectedSemiKeys.length) setSelectedSemiKeys(validKeys);
      return;
    }
    if (selectedSemiKeys.length > 0) setSelectedSemiKeys([]);
  }, [semiOnlySummary, selectedSemiKeys]);

  useEffect(() => {
    if (semiSummary.length === 0) return;
    setPrintSemiListSelection((prev) => {
      const next: Record<string, boolean> = {};
      semiSummary.forEach((semi) => {
        next[semi.key] = prev[semi.key] !== undefined ? prev[semi.key] : true;
      });
      return next;
    });
  }, [semiSummary]);

  const toggleSemiDetailSelection = (semiKey: string) => {
    if (!semiOnlySummary.some((semi) => semi.key === semiKey)) return;
    setSelectedSemiKeys((prev) =>
      prev.includes(semiKey) ? prev.filter((key) => key !== semiKey) : [...prev, semiKey]
    );
  };

  const setAllSemiDetailSelection = (selected: boolean) => {
    const targetRows = (semiSearch.trim() ? filteredSemiSummary : semiSummary).filter((semi) => semi.semiId != null);
    const targetKeys = targetRows.map((semi) => semi.key);
    setSelectedSemiKeys((prev) => {
      if (!selected) return prev.filter((key) => !targetKeys.includes(key));
      const next = new Set(prev);
      targetKeys.forEach((key) => next.add(key));
      return Array.from(next);
    });
  };

  const isSemiListItemSelected = (key: string) => printSemiListSelection[key] !== false;
  const toggleSemiListItem = (key: string) => {
    setPrintSemiListSelection((prev) => ({ ...prev, [key]: !(prev[key] !== false) }));
  };
  const setAllSemiListSelection = (selected: boolean) => {
    setPrintSemiListSelection((prev) => {
      const next = { ...prev };
      const targetRows = semiSearch.trim() ? filteredSemiSummary : semiSummary;
      targetRows.forEach((semi) => { next[semi.key] = selected; });
      return next;
    });
  };

  const selectedSemiDetailBlocks = useMemo(
    () =>
      selectedSemiKeys
        .map((key) => {
          const summary = semiOnlySummary.find((semi) => semi.key === key);
          if (!summary) return null;
          return {
            summary,
            detailRows: buildSemiDetailRows(summary.semiId),
          };
        })
        .filter(Boolean) as Array<{
        summary: { key: string; semiId: number | null; semiName: string; categoryName: string; totalQty: number; qtyBySlot: Record<string, number> };
        detailRows: SemiDetailRow[];
      }>,
    [selectedSemiKeys, semiOnlySummary, buildSemiDetailRows]
  );

  const exportExcel = () => {
    if (filteredRows.length === 0 || slotColumns.length === 0) {
      window.alert('Немає даних для експорту.');
      return;
    }

    const exportRows: Array<Array<string | number>> = [];
    exportRows.push([
      'Бланк підготовки до виконання робіт',
      ...Array(Math.max(slotColumns.length + 1, 1)).fill(''),
    ]);
    exportRows.push([
      `Замовлень вибрано: ${selectedOrders.length}`,
      ...Array(Math.max(slotColumns.length + 1, 1)).fill(''),
    ]);
    exportRows.push([
      `Загальний час приготування: ${formatDurationLabel(totalPreparationTimeSeconds)}`,
      `Загальний час на видачу: ${formatDurationLabel(totalServeTimeSeconds)}`,
      ...Array(Math.max(slotColumns.length, 1)).fill(''),
    ]);
    exportRows.push([]);

    filteredRows.forEach((row) => {
      exportRows.push([`Страва: ${formatPrepDishName(row.dishId, row.dishName)}`, `Всього порцій: ${row.totalPortions.toFixed(2)}`, ...Array(slotColumns.length).fill('')]);
      exportRows.push([
        `Час приготування (1 порція): ${formatDurationLabel(row.cookingTimePerPortion)}`,
        `Потрібно на всі порції: ${formatDurationLabel(calcRequiredTime(row.cookingTimePerPortion, row.totalPortions))}`,
        ...Array(slotColumns.length).fill(''),
      ]);
      exportRows.push([
        'Інгредієнт',
        'Кількість на 1 порцію',
        ...slotColumns.map((column) => `${column.dateLabel} | ${column.venueLabel}`),
      ]);
      exportRows.push([
        '',
        '',
        ...slotColumns.map((column) => `Порції: ${(row.portionsBySlot[column.key] || 0).toFixed(2)}`),
      ]);

      if (row.semiItems.length === 0) {
        exportRows.push(['—', '—', ...slotColumns.map(() => '—')]);
      } else {
        row.semiItems.forEach((semi) => {
          exportRows.push([
            semi.name,
            semi.piecesPerPortion != null ? formatPreparationQuantity(semi.qtyPerPortion, semi.piecesPerPortion) : Number(semi.qtyPerPortion.toFixed(4)),
            ...slotColumns.map((column) => {
              const slotPortions = row.portionsBySlot[column.key] || 0;
              const slotTotal = semi.qtyPerPortion * slotPortions;
              return slotPortions > 0 ? semi.piecesPerPortion != null ? formatPreparationQuantity(semi.qtyPerPortion, semi.piecesPerPortion, slotPortions) : Number(slotTotal.toFixed(4)) : '—';
            }),
          ]);
        });
      }
      exportRows.push([]);
    });

    const ws = XLSX.utils.aoa_to_sheet(exportRows);
    ws['!cols'] = [{ wch: 34 }, { wch: 22 }, ...slotColumns.map(() => ({ wch: 24 }))];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Бланк підготовки');
    XLSX.writeFile(wb, `preparation_worksheet_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  type PrintableDishRow = { row: PrepDishRow; selectedSemiItems: PrepSemiItem[] };

  const openDishPrintWindow = (printableRows: PrintableDishRow[], options?: { title?: string }) => {
    if (printableRows.length === 0 || slotColumns.length === 0) return;

    const selectedDishSemiMap = new Map<number, {
      key: string;
      semiId: number;
      semiName: string;
      categoryName: string;
      totalQty: number;
      qtyBySlot: Record<string, number>;
    }>();

    printableRows.forEach(({ row }) => {
      row.semiItems.forEach((semi) => {
        if (semi.id == null || semi.qtyPerPortion <= 0) return;
        const semiId = semi.id;
        if (!selectedDishSemiMap.has(semiId)) {
          const semiRef = semiById.get(semiId);
          selectedDishSemiMap.set(semiId, {
            key: `dish-print:${semiId}`,
            semiId,
            semiName: semiRef?.name || semi.name || `Н/Ф #${semiId}`,
            categoryName: semiRef?.categoryName?.trim() || 'Без категорії',
            totalQty: 0,
            qtyBySlot: {},
          });
        }
        const summary = selectedDishSemiMap.get(semiId)!;
        slotColumns.forEach((column) => {
          const slotPortions = row.portionsBySlot[column.key] || 0;
          if (slotPortions <= 0) return;
          const requiredQty = semi.qtyPerPortion * slotPortions;
          if (requiredQty <= 0) return;
          summary.totalQty += requiredQty;
          summary.qtyBySlot[column.key] = (summary.qtyBySlot[column.key] || 0) + requiredQty;
        });
      });
    });

    const selectedDishSemiDetailBlocks = includeSemiDetailsInDishPrint
      ? Array.from(selectedDishSemiMap.values())
          .sort((a, b) => a.semiName.localeCompare(b.semiName, 'uk'))
          .map((summary) => ({
            summary,
            detailRows: buildSemiDetailRows(summary.semiId),
          }))
      : [];

    const escapeHtml = (value: string) =>
      value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    const renderDishBlock = (row: PrepDishRow, selectedSemiItems: PrepSemiItem[]) => {
        const totalCookingTime = calcRequiredTime(row.cookingTimePerPortion, row.totalPortions);
        const totalServeTime = calcRequiredTime(row.serveTimePerPortion, row.totalPortions);
        const ingredientRows = selectedSemiItems.length
          ? selectedSemiItems.map((semi) => {
              const qtyByVenue = slotColumns
                .map((column) => {
                  const slotPortions = row.portionsBySlot[column.key] || 0;
                  return {
                    venue: column.venueLabel,
                    date: column.dateLabel,
                    qty: semi.qtyPerPortion * slotPortions,
                  };
                })
                .filter((v) => v.qty > 0);
              const tooltipForTotal = qtyByVenue
                .map((v) => `${escapeHtml(v.venue)} (${v.date}): ${v.qty.toFixed(4)}`)
                .join('\n');
              return `<tr>
              <td class="col-name">${escapeHtml(semi.name)}</td>
              <td class="col-compact" style="text-align:right;">${formatPreparationQuantity(semi.qtyPerPortion, semi.piecesPerPortion)}</td>
              <td class="col-compact" style="text-align:right;font-weight:700;color:#16a085;" title="${tooltipForTotal}">${formatPreparationQuantity(semi.qtyPerPortion, semi.piecesPerPortion, row.totalPortions)}</td>
              ${slotColumns.map((column, index) => {
                const slotPortions = row.portionsBySlot[column.key] || 0;
                const dayTone = index % 2 === 0 ? 'day-a' : 'day-b';
                return `<td class="col-slot ${dayTone}">${slotPortions > 0 ? `<strong>${formatPreparationQuantity(semi.qtyPerPortion, semi.piecesPerPortion, slotPortions)}</strong>` : '—'}</td>`;
              }).join('')}
            </tr>`;
            })
          : [`<tr><td>—</td><td style="text-align:right;">—</td><td>—</td>${slotColumns.map(() => '<td>—</td>').join('')}</tr>`];

        return `<div class="dish-block">
          <h3>${escapeHtml(formatPrepDishName(row.dishId, row.dishName))}</h3>
          <div class="meta">Всього порцій: ${row.totalPortions.toFixed(2)}</div>
          <div class="meta">Час приготування: ${formatDurationLabel(row.cookingTimePerPortion)} / порція · Загалом: ${formatDurationLabel(totalCookingTime)}</div>
          <div class="meta">Час на видачу: ${formatDurationLabel(row.serveTimePerPortion)} / порція · Загалом: ${formatDurationLabel(totalServeTime)}</div>
          <table class="print-table prep-dish-table">
            <thead>
              <tr>
                <th class="col-name ingredient-head">Інгредієнт</th>
                <th class="col-compact" style="white-space:normal;line-height:1.1;">К-сть<br/>на 1 порц.</th>
                <th class="col-compact" style="white-space:normal;line-height:1.1;color:#16a085;font-weight:700">Загалом</th>
                ${slotColumns
                  .map(
                    (column, index) => {
                      const dayTone = index % 2 === 0 ? 'day-a' : 'day-b';
                      return `<th class="slot-head ${dayTone}">${escapeHtml(column.dateLabel)}<br/><span style="font-weight:600">${escapeHtml(compactVenuePrintLabel(column.venueLabel))}</span><br/><span style="font-weight:700;color:#1f2937">Порції:<br/>${(row.portionsBySlot[column.key] || 0).toFixed(2)}</span></th>`;
                    }
                  )
                  .join('')}
              </tr>
            </thead>
            <tbody>
              ${ingredientRows.join('')}
            </tbody>
          </table>
        </div>`;
    };

    const byCategory = new Map<string, Array<{ row: PrepDishRow; selectedSemiItems: PrepSemiItem[] }>>();
    printableRows.forEach((item) => {
      const categoryName = item.row.category?.trim() || 'Без категорії';
      if (!byCategory.has(categoryName)) byCategory.set(categoryName, []);
      byCategory.get(categoryName)!.push(item);
    });

    const blocksHtml = Array.from(byCategory.entries())
      .sort((a, b) => a[0].localeCompare(b[0], 'uk'))
      .map(([categoryName, items], index) => `
        <section class="category-section${index > 0 ? ' category-page-break' : ''}">
          <h2 class="category-title">${escapeHtml(categoryName)}</h2>
          ${items.map(({ row, selectedSemiItems }) => renderDishBlock(row, selectedSemiItems)).join('')}
        </section>
      `)
      .join('');

    const semiDetailsFromSelectedDishesHtml = selectedDishSemiDetailBlocks.length > 0
      ? `
        <section class="semi-details-section">
          <h2 class="category-title">Калькуляції Н/Ф по обраних стравах</h2>
          ${selectedDishSemiDetailBlocks.map(({ summary, detailRows }) => {
            const detailRowsHtml = detailRows.map((item) => `<tr>
              <td class="col-name">${escapeHtml(`${item.itemType === 'semi' ? 'Н/Ф: ' : ''}${item.itemName}`)}</td>
              <td class="col-compact" style="text-align:right;">${item.qtyPerSemiUnit.toFixed(4)}</td>
              <td class="col-compact" style="text-align:right;font-weight:700;color:#16a085;">${(summary.totalQty * item.qtyPerSemiUnit).toFixed(4)}</td>
              ${slotColumns.map((column) => {
                const semiQty = summary.qtyBySlot[column.key] || 0;
                return `<td class="col-slot">${(semiQty * item.qtyPerSemiUnit).toFixed(4)}</td>`;
              }).join('')}
            </tr>`).join('');

            return `<div class="semi-detail-block">
              <h3>${escapeHtml(summary.semiName)}</h3>
              <div class="meta">Всього потрібно Н/Ф: ${summary.totalQty.toFixed(4)}</div>
              <table class="print-table">
                <thead>
                  <tr>
                    <th class="col-name">Позиція</th>
                    <th class="col-compact">На 1 од. н/ф</th>
                    <th class="col-compact" style="color:#16a085;font-weight:700;">Загалом</th>
                    ${slotColumns.map((column) => `<th>${escapeHtml(column.dateLabel)}<br/><span style="font-weight:600">${escapeHtml(compactVenuePrintLabel(column.venueLabel))}</span></th>`).join('')}
                  </tr>
                </thead>
                <tbody>${detailRowsHtml || `<tr><td colspan="${3 + slotColumns.length}">Немає розкладки для цього н/ф</td></tr>`}</tbody>
                <tfoot>
                  <tr>
                    <td colspan="3" style="text-align:right;font-weight:700;background:#eff6ff;color:#1e3a8a;">Н/Ф по днях:</td>
                    ${slotColumns.map((column) => `<td class="col-slot" style="font-weight:700;background:#eff6ff;color:#1e3a8a;">Н/Ф: ${(summary.qtyBySlot[column.key] || 0).toFixed(4)}</td>`).join('')}
                  </tr>
                </tfoot>
              </table>
            </div>`;
          }).join('')}
        </section>
      `
      : '';
    const pageTitle = options?.title || 'Бланк підготовки';
    const html = `<!DOCTYPE html><html lang="uk"><head><meta charset="utf-8"/>
      <title>${escapeHtml(pageTitle)}</title>
      <style>
        body{font-family:Arial,sans-serif;padding:20px;color:#1f2937;}
        body,*{print-color-adjust:exact;-webkit-print-color-adjust:exact;}
        h1{margin:0 0 8px;}
        h2{margin:14px 0 6px;}
        h3{margin:0 0 6px;}
        .meta{font-size:12px;color:#4b5563;margin-bottom:4px;}
        .category-section{margin-top:16px;padding:10px 12px;border:1px solid #cbd5e1;border-radius:10px;background:#f8fafc;break-inside:avoid-page;page-break-inside:avoid;}
        .category-section.category-page-break{break-before:page;page-break-before:always;}
        .category-title{padding:6px 10px;border-radius:8px;background:#334155;color:#fff;display:inline-block;}
        .dish-block{margin-top:14px;break-inside:avoid-page;page-break-inside:avoid;}
        table.print-table{width:100%;border-collapse:collapse;margin-top:12px;table-layout:fixed;}
        ${printTableLayoutCss}
        .prep-dish-table tr{break-inside:avoid-page;page-break-inside:avoid;}
        .prep-dish-table th.ingredient-head{background:#334155;color:#fff;}
        .prep-dish-table td.col-name{background:#f8fafc;border-left:3px solid #94a3b8;font-weight:600;}
        .prep-dish-table tbody tr:nth-child(odd) td.col-name{background:#eef2f7;}
        .prep-dish-table th.slot-head.day-a,.prep-dish-table td.col-slot.day-a{background:#f3f0ff;}
        .prep-dish-table th.slot-head.day-b,.prep-dish-table td.col-slot.day-b{background:#ecfeff;}
        .prep-dish-table th.slot-head{border-bottom:2px solid #9ca3af;}
        .prep-dish-table .source-notes{width:30%;font-style:italic;color:#6b7280;}
        .semi-details-section{margin-top:18px;break-before:page;page-break-before:always;}
        .semi-detail-block{margin-top:14px;break-inside:avoid-page;page-break-inside:avoid;}
      </style></head><body>
      <h1>${escapeHtml(pageTitle)}</h1>
      <div class="meta">Замовлень вибрано: ${selectedOrders.length}</div>
      <div class="meta">Загальний час приготування: ${formatDurationLabel(totalPreparationTimeSeconds)} · На видачу: ${formatDurationLabel(totalServeTimeSeconds)}</div>
      <div class="meta">Сформовано: ${new Date().toLocaleString('uk-UA')}</div>
      <div class="meta">Режим підрахунку ваги: ${worksheetQuantityMode === 'netto' ? 'Нетто' : worksheetQuantityMode === 'brutto' ? 'Брутто' : 'Вихід'}</div>
      ${blocksHtml}
      ${semiDetailsFromSelectedDishesHtml}
      </body></html>`;

    void openAndPrintHtml(html, { windowFeatures: 'width=1200,height=800', minimumDelayMs: 300 });
  };

  const exportPdf = () => {
    if (filteredRows.length === 0 || slotColumns.length === 0) {
      window.alert('Немає даних для експорту.');
      return;
    }

    const printableRows = filteredRows
      .map((row) => ({
        row,
        selectedSemiItems: row.semiItems.filter((semi) => isPrintItemSelected(row.dishId, semi.name)),
      }))
      .filter((item) => isDishSelectedForPrint(item.row.dishId) && (item.row.semiItems.length === 0 || item.selectedSemiItems.length > 0));

    if (printableRows.length === 0) {
      window.alert('Оберіть хоча б один блок страви і позиції для друку.');
      return;
    }

    openDishPrintWindow(printableRows);
  };

  const [aggregateSummaryCollapsed, setAggregateSummaryCollapsed] = useState(false);
  const setVenueColor = (venue: string, color: string) => {
    setCustomVenueColors((current) => ({ ...current, [venue]: color }));
  };
  const resetVenueColor = (venue: string) => {
    setCustomVenueColors((current) => {
      const next = { ...current };
      delete next[venue];
      return next;
    });
  };

  const printAggregateDishSummary = useCallback(() => {
    if (selectedOrders.length === 0 || slotColumns.length === 0) {
      window.alert('Оберіть хоча б одне підтверджене замовлення.');
      return;
    }

    const venues = Array.from(new Set(slotColumns.map((column) => column.venueLabel)));
    const venueColorMap = new Map<string, string>();
    venues.forEach((venue, index) => {
      venueColorMap.set(venue, aggregateVenueColors.get(venue) || getDefaultVenueColor(venue, index));
    });
    const escapeHtml = (value: string) =>
      value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    const rowsByCategory = aggregateDishRowsByCategory;
    const rowsHtml = rowsByCategory.map(({ categoryName, rows }) => {
      const categoryRows = rows.map((row) => {
        const venueParts = venues
          .map((venue) => {
            const qty = slotColumns
              .filter((column) => column.venueLabel === venue)
              .reduce((sum, column) => sum + (row.portionsByVenue[column.key] || 0), 0);
            if (qty <= 0) return '';
            const color = venueColorMap.get(venue) || '#374151';
            return `<span style="color:${color};font-weight:700;">${qty.toFixed(2)}</span>`;
          })
          .filter(Boolean);

        return `<tr>
          <td>${escapeHtml(row.dishName)}</td>
          <td style="text-align:right;font-weight:700;">${row.totalPortions.toFixed(2)}</td>
          <td>${venueParts.length > 0 ? venueParts.join(' + ') : '—'}</td>
        </tr>`;
      }).join('');

      return `<tr><td colspan="3" style="background:#334155;color:#fff;font-weight:700;">${escapeHtml(categoryName)}</td></tr>${categoryRows}`;
    }).join('');

    const venueLegendHtml = venues.map((venue) => {
      const color = venueColorMap.get(venue) || '#374151';
      return `<span style="display:inline-flex;align-items:center;gap:6px;margin-right:12px;margin-bottom:6px;"><span style="width:10px;height:10px;border-radius:50%;background:${color};display:inline-block;"></span>${escapeHtml(venue)}</span>`;
    }).join('');

    const html = `<!DOCTYPE html><html lang="uk"><head><meta charset="utf-8"/>
      <title>Загальний список страв</title>
      <style>
        body{font-family:Arial,sans-serif;padding:20px;color:#1f2937;}
        body,*{print-color-adjust:exact;-webkit-print-color-adjust:exact;}
        h1{margin:0 0 8px;}
        .meta{font-size:12px;color:#4b5563;margin-bottom:4px;}
        .legend{margin:10px 0 12px;font-size:12px;color:#374151;display:flex;flex-wrap:wrap;gap:6px;}
        table{width:100%;border-collapse:collapse;table-layout:fixed;margin-top:10px;}
        th,td{border:1px solid #d1d5db;padding:7px 8px;font-size:12px;vertical-align:top;}
        th{background:#f3f4f6;text-align:left;}
        td:last-child{white-space:normal;word-break:break-word;}
        tfoot td{font-weight:700;background:#ecfdf5;color:#065f46;}
      </style></head><body>
      <h1>Загальний список страв по обраних замовленнях</h1>
      <div class="meta">Замовлень вибрано: ${selectedOrders.length}</div>
      <div class="meta">Загальна кількість страв: ${aggregateTotalPortions.toFixed(2)}</div>
      <div class="legend"><strong>Кольори місць проведення:</strong>${venueLegendHtml}</div>
      <table>
        <thead>
          <tr>
            <th>Страва</th>
            <th style="width:120px;text-align:right;">Усього порцій</th>
            <th>По місцях проведення</th>
          </tr>
        </thead>
        <tbody>${rowsHtml || '<tr><td colspan="3">Немає даних</td></tr>'}</tbody>
        <tfoot>
          <tr>
            <td>Загальна кількість</td>
            <td style="text-align:right;">${aggregateTotalPortions.toFixed(2)}</td>
            <td></td>
          </tr>
        </tfoot>
      </table>
      </body></html>`;

    void openAndPrintHtml(html, { windowFeatures: 'width=1200,height=800', minimumDelayMs: 300 });
  }, [aggregateDishRowsByCategory, aggregateTotalPortions, aggregateVenueColors, selectedOrders.length, slotColumns]);

  const printSingleDish = (row: PrepDishRow) => {
    openDishPrintWindow(
      [{ row, selectedSemiItems: row.semiItems }],
      { title: `Бланк підготовки: ${formatPrepDishName(row.dishId, row.dishName)}` }
    );
  };

  const exportSemiDetailsOnlyPdf = () => {
    if (selectedSemiDetailBlocks.length === 0 || slotColumns.length === 0) {
      window.alert('Оберіть хоча б один напівфабрикат для деталізації.');
      return;
    }

    const escapeHtml = (value: string) =>
      value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    const detailBlocksHtml = selectedSemiDetailBlocks.map(({ summary, detailRows }) => {
      const rows = detailRows.map((item) => `<tr>
        <td class="col-name">${escapeHtml(`${item.itemType === 'semi' ? 'Н/Ф: ' : ''}${item.itemName}`)}</td>
        <td class="col-compact" style="text-align:right;">${item.qtyPerSemiUnit.toFixed(4)}</td>
        <td class="col-compact" style="text-align:right;font-weight:700;color:#16a085;">${(summary.totalQty * item.qtyPerSemiUnit).toFixed(4)}</td>
        ${slotColumns.map((column) => {
          const semiQty = summary.qtyBySlot[column.key] || 0;
          return `<td class="col-slot">${(semiQty * item.qtyPerSemiUnit).toFixed(4)}</td>`;
        }).join('')}
      </tr>`).join('');

      return `<div class="block">
        <h2>${escapeHtml(summary.semiName)}</h2>
        <table class="print-table">
          <thead>
            <tr>
              <th class="col-name">Позиція</th>
              <th class="col-compact">На 1 од. н/ф</th>
              <th class="col-compact" style="color:#16a085;font-weight:700;">Загалом</th>
              ${slotColumns.map((column) => `<th>${escapeHtml(column.dateLabel)}<br/><span style="font-weight:600">${escapeHtml(compactVenuePrintLabel(column.venueLabel))}</span></th>`).join('')}
            </tr>
          </thead>
          <tbody>${rows || `<tr><td colspan="${3 + slotColumns.length}">Немає розкладки для цього н/ф</td></tr>`}</tbody>
          <tfoot>
            <tr>
              <td colspan="3" style="text-align:right;font-weight:700;background:#eff6ff;color:#1e3a8a;">
                Н/Ф по днях:
              </td>
              ${slotColumns.map((column) => `<td class="col-slot" style="font-weight:700;background:#eff6ff;color:#1e3a8a;">Н/Ф: ${(summary.qtyBySlot[column.key] || 0).toFixed(4)}</td>`).join('')}
            </tr>
            <tr>
              <td colspan="${3 + slotColumns.length}" style="text-align:right;font-weight:700;background:#ecfdf5;color:#065f46;">
                Всього потрібно н/ф: ${summary.totalQty.toFixed(4)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>`;
    }).join('');

    const html = `<!DOCTYPE html><html lang="uk"><head><meta charset="utf-8"/>
      <title>Деталізація напівфабрикатів</title>
      <style>
        body{font-family:Arial,sans-serif;padding:20px;color:#1f2937;}
        body,*{print-color-adjust:exact;-webkit-print-color-adjust:exact;}
        h1,h2{margin:0 0 8px;}
        .meta{font-size:12px;color:#4b5563;margin-bottom:6px;}
        .block{margin-top:20px;break-inside:avoid;page-break-inside:avoid;}
        table.print-table{width:100%;border-collapse:collapse;margin-top:8px;table-layout:fixed;}
        ${printTableLayoutCss}
        @media print{body,*{print-color-adjust:exact;-webkit-print-color-adjust:exact;} .block{break-inside:avoid;page-break-inside:avoid;} table.print-table{break-inside:auto;page-break-inside:auto;} tr{break-inside:auto;page-break-inside:auto;}}
      </style></head><body>
      <h1>Деталізовані напівфабрикати</h1>
      <div class="meta">Замовлень вибрано: ${selectedOrders.length}</div>
      <div class="meta">Сформовано: ${new Date().toLocaleString('uk-UA')}</div>
      ${detailBlocksHtml}
      </body></html>`;

    void openAndPrintHtml(html, { windowFeatures: 'width=1200,height=800', minimumDelayMs: 300 });
  };

  const exportSemiListOnlyPdf = () => {
    const selectedRows = semiSummary.filter((semi) => isSemiListItemSelected(semi.key));
    if (selectedRows.length === 0 || slotColumns.length === 0) {
      window.alert('Оберіть хоча б одну позицію для друку.');
      return;
    }

    const escapeHtml = (value: string) =>
      value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\x22/g, '&quot;')
        .replace(/'/g, '&#39;');

    const selectedSemiRows = selectedRows.filter((semi) => semi.semiId != null);
    const selectedProductRows = selectedRows.filter((semi) => semi.semiId == null);
    const groupRowsByCategory = (rowsToRender: typeof selectedRows) => {
      const grouped = new Map<string, typeof selectedRows>();
      rowsToRender.forEach((row) => {
        const categoryName = row.categoryName?.trim() || 'Без категорії';
        const bucket = grouped.get(categoryName) ?? [];
        bucket.push(row);
        grouped.set(categoryName, bucket);
      });
      return Array.from(grouped.entries())
        .sort((a, b) => a[0].localeCompare(b[0], 'uk'))
        .map(([categoryName, rows]) => [categoryName, rows.sort((a, b) => a.semiName.localeCompare(b.semiName, 'uk'))] as const);
    };
    const buildGroupedSummaryRowsHtml = (rowsToRender: typeof selectedRows) => {
      const groups = groupRowsByCategory(rowsToRender);
      if (groups.length === 0) return `<tr><td colspan="${2 + slotColumns.length}">Немає даних</td></tr>`;
      return groups.map(([categoryName, rows]) => `
        <tr>
          <td class="category-band" colspan="${2 + slotColumns.length}">
            Категорія: ${escapeHtml(categoryName)}
          </td>
        </tr>
        ${rows.map((semi) => `<tr>
          <td class="col-name">${escapeHtml(semi.semiName)}</td>
          <td class="col-compact" style="text-align:right;"><strong>${semi.totalQty.toFixed(4)}</strong></td>
          ${slotColumns.map((column, index) => `<td class="${index % 2 === 0 ? 'day-a' : 'day-b'}">${(semi.qtyBySlot[column.key] || 0).toFixed(4)}</td>`).join('')}
        </tr>`).join('')}
      `).join('');
    };

    const html = `<!DOCTYPE html><html lang="uk"><head><meta charset="utf-8"/>
      <title>Загальний список напівфабрикатів</title>
      <style>
        body{font-family:Arial,sans-serif;padding:20px;color:#1f2937;}
        body,*{print-color-adjust:exact;-webkit-print-color-adjust:exact;}
        h1{margin:0 0 8px;}
        h2{margin:12px 0 6px;padding:6px 10px;border-radius:8px;background:#334155;color:#fff;display:inline-block;}
        .meta{font-size:12px;color:#4b5563;margin-bottom:4px;}
        table.print-table{width:100%;border-collapse:collapse;margin:10px 0;table-layout:fixed;}
        ${printTableLayoutCss}
        th.day-a,td.day-a{background:#f3f0ff;}
        th.day-b,td.day-b{background:#ecfeff;}
        td.col-name{background:#f8fafc;border-left:3px solid #94a3b8;font-weight:600;}
        td.category-band{background:#dbeafe !important;color:#1e3a8a;font-weight:700;}
        .print-page-break{break-before:page;page-break-before:always;}
      </style></head><body>
      <h1>Загальна потреба по напівфабрикатах</h1>
      <div class="meta">Замовлень вибрано: ${selectedOrders.length}</div>
      <div class="meta">Сформовано: ${new Date().toLocaleString('uk-UA')}</div>
      <h2>Напівфабрикати</h2>
      <table class="print-table">
        <thead><tr>
          <th class="col-name">Напівфабрикат</th>
          <th class="col-compact" style="white-space:normal;line-height:1.1;">Всього<br/>потрібно</th>
          ${slotColumns.map((column, index) => `<th class="${index % 2 === 0 ? 'day-a' : 'day-b'}">${escapeHtml(column.dateLabel)}<br/><span style="font-weight:600">${escapeHtml(compactVenuePrintLabel(column.venueLabel))}</span></th>`).join('')}
        </tr></thead>
        <tbody>${buildGroupedSummaryRowsHtml(selectedSemiRows)}</tbody>
      </table>
      <div class="print-page-break"></div>
      <h2>Продукти</h2>
      <table class="print-table">
        <thead><tr>
          <th class="col-name">Продукт</th>
          <th class="col-compact" style="white-space:normal;line-height:1.1;">Всього<br/>потрібно</th>
          ${slotColumns.map((column, index) => `<th class="${index % 2 === 0 ? 'day-a' : 'day-b'}">${escapeHtml(column.dateLabel)}<br/><span style="font-weight:600">${escapeHtml(compactVenuePrintLabel(column.venueLabel))}</span></th>`).join('')}
        </tr></thead>
        <tbody>${buildGroupedSummaryRowsHtml(selectedProductRows)}</tbody>
      </table>
      </body></html>`;

    void openAndPrintHtml(html, { windowFeatures: 'width=1200,height=800', minimumDelayMs: 300 });
  };

  const printByOperations = () => {
    if (rows.length === 0) {
      window.alert('Спочатку сформуйте бланк.');
      return;
    }

    type EntrySource = { 
      sourceName: string; 
      qtyTotal: number; 
      pieces: number | null;
      operationNotes: string; 
      cardNotes: string; 
      venue?: string;
      dishId?: number;
      slotKey?: string;
    };
    type IngredientGroup = { ingredientName: string; weightType: string; entries: EntrySource[]; totalQty: number };
    type OperationGroup = { opId: number; opName: string; opColor: string; ingredientGroups: Map<string, IngredientGroup> };
    const opGroups = new Map<string, OperationGroup>();

    const addToOp = (opId: number, opName: string, opColor: string, ingredientName: string, ingredientKind: 'product' | 'semi', ingredientId: number | null, weightType: string, sourceName: string, qty: number, operationNotes: string, cardNotes: string, venue?: string, dishId?: number, slotKey?: string, pieces: number | null = null) => {
      if (qty <= 0) return;
      const opKey = String(opId);
      if (!opGroups.has(opKey)) opGroups.set(opKey, { opId, opName, opColor, ingredientGroups: new Map() });
      const opGroup = opGroups.get(opKey)!;
      const ingKey = `${getPreparationIngredientGroupKey(ingredientKind, ingredientId, weightType, ingredientName)}|${pieces != null ? 'pieces' : 'weight'}`;
      if (!opGroup.ingredientGroups.has(ingKey)) {
        opGroup.ingredientGroups.set(ingKey, { ingredientName: ingredientName.trim(), weightType: weightType.trim(), entries: [], totalQty: 0 });
      }
      const ingGroup = opGroup.ingredientGroups.get(ingKey)!;
      ingGroup.entries.push({ sourceName, qtyTotal: qty, pieces, operationNotes, cardNotes, venue, dishId, slotKey });
      ingGroup.totalQty += qty;
    };

    // Collect from dish ingredients - track portions by slot and dish
    const dishPortionsBySlot = new Map<number, Record<string, number>>();
    rows.forEach((row) => {
      const dish = dishById.get(row.dishId);
      if (!dish) return;
      dishPortionsBySlot.set(row.dishId, { ...row.portionsBySlot });
      dish.ingredients.forEach((ingredient) => {
        if (!ingredient.preparationOperationId) return;
        const opId = ingredient.preparationOperationId;
        const op = preparationOperations.find((o) => o.id === opId);
        const effectiveQty = resolveItemQty(ingredient);
        const ingredientName = ingredient.productName || ingredient.semiFinishedProductName || '?';
        // Create entry for each slot (venue) where this dish appears
        slotColumns.forEach((slot) => {
          const slotPortions = row.portionsBySlot[slot.key] || 0;
          if (slotPortions > 0) {
            const slotVenueLabel = slot.venueLabel || slot.dateLabel;
            addToOp(
              opId, ingredient.preparationOperationName || `Операція #${opId}`, op?.color || '#607d8b',
              ingredientName,
              ingredient.productId != null ? 'product' : 'semi',
              ingredient.productId ?? ingredient.semiFinishedProductId,
              ingredient.productId != null
                ? products.find((product) => product.id === ingredient.productId)?.weightType || ''
                : semiById.get(ingredient.semiFinishedProductId ?? -1)?.weightType || '',
              `${formatPrepDishName(row.dishId, row.dishName)}`,
              effectiveQty * slotPortions, ingredient.operationNotes || '', ingredient.notes || '', slotVenueLabel, row.dishId, slot.key,
              preparationPieceCount(ingredient.piecesPerPortion, slotPortions)
            );
          }
        });
      });
    });
    // Collect from semi-finished items
    semiOnlySummary.forEach((semiSummaryRow) => {
      if (semiSummaryRow.semiId == null) return;
      const semi = semiById.get(semiSummaryRow.semiId);
      if (!semi) return;
      const totalSemiNeeded = semiSummaryRow.totalQty;
      semi.items.forEach((item) => {
        if (!item.preparationOperationId) return;
        const opId = item.preparationOperationId;
        const op = preparationOperations.find((o) => o.id === opId);
        const itemQtyPerUnit = resolveItemQty(item);
        const totalItemQty = itemQtyPerUnit * totalSemiNeeded;
        const ingredientName = item.productName || item.subSemiFinishedProductName || '?';
        addToOp(
          opId, item.preparationOperationName || `Операція #${opId}`, op?.color || '#607d8b',
          ingredientName,
          item.productId != null ? 'product' : 'semi',
          item.productId ?? item.subSemiFinishedProductId,
          item.productId != null
            ? products.find((product) => product.id === item.productId)?.weightType || ''
            : semiById.get(item.subSemiFinishedProductId ?? -1)?.weightType || '',
          `Н/Ф «${semi.name}» (${totalSemiNeeded.toFixed(3)})`,
          totalItemQty, item.operationNotes || '', item.notes || ''
        );
      });
    });

    if (opGroups.size === 0) {
      window.alert('Жодних мічених операцій не знайдено. Призначте операції інгредієнтам у стравах або н/ф, після чого сформуйте бланк повторно.');
      return;
    }

    const escapeHtml = (value: string) =>
      value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

    const sortedOps = Array.from(opGroups.values())
      .sort((a, b) => {
        const aOp = preparationOperations.find((o) => o.id === a.opId);
        const bOp = preparationOperations.find((o) => o.id === b.opId);
        const bySort = (aOp?.sortOrder ?? 999) - (bOp?.sortOrder ?? 999);
        if (bySort !== 0) return bySort;
        return a.opName.localeCompare(b.opName, 'uk');
      });

    const venueLabels = Array.from(new Set(
      slotColumns.map((slot) => slot.venueLabel || slot.dateLabel)
    )).sort((a, b) => a.localeCompare(b, 'uk'));
    const venueColorByLabel = new Map(venueLabels.map((label, index) => [
      label,
      aggregateVenueColors.get(label) || getDefaultVenueColor(label, index),
    ]));
    const venueLegendHtml = venueLabels.length > 1 ? `
        <div class="legend">
          <div class="legend-title">Позначення місць проведення:</div>
          ${venueLabels.map((venueLabel) => {
            const color = venueColorByLabel.get(venueLabel) || '#000';
            return `<div class="legend-item"><span class="legend-color" style="background-color:${color};"></span> ${escapeHtml(venueLabel)}</div>`;
          }).join('')}
        </div>` : '';

    const blocksHtml = sortedOps.map((opGroup, opIdx) => {
      const sortedIngredients = Array.from(opGroup.ingredientGroups.values())
        .sort((a, b) => a.ingredientName.localeCompare(b.ingredientName, 'uk'));

      const rowsHtml = sortedIngredients.map((ingGroup) => {
        // Group entries by venue (slot label)
        const venueMap = new Map<string, EntrySource[]>();
        ingGroup.entries.forEach((entry) => {
          const venueKey = entry.venue || '';
          if (!venueMap.has(venueKey)) venueMap.set(venueKey, []);
          venueMap.get(venueKey)!.push(entry);
        });

        // Group entries by sourceName to aggregate venues for the same source
        const sourceGroupMap = new Map<string, EntrySource[]>();
        ingGroup.entries.forEach((entry) => {
          if (!sourceGroupMap.has(entry.sourceName)) sourceGroupMap.set(entry.sourceName, []);
          sourceGroupMap.get(entry.sourceName)!.push(entry);
        });

        // Check if we have multiple venues
        const hasMultipleVenues = venueMap.size > 1;
        const venues = Array.from(venueMap.keys()).filter(v => v).sort();
        const sourcesHtml = Array.from(sourceGroupMap.entries()).map(([sourceName, sourceEntries]) => {
          // Determine the venue(s) for this source group
          const sourceVenues = new Set(sourceEntries.map(e => e.venue || '').filter(v => v));
          const firstSourceVenue = Array.from(sourceVenues)[0];
          let sourceColor = '#000'; // default black
           
          // Find color index for this source's venue
          if (firstSourceVenue && sourceVenues.size === 1) {
            sourceColor = venueColorByLabel.get(firstSourceVenue) || '#000';
          }

          // Calculate portion breakdown for this source (if it's a dish from multiple venues/slots)
          let displaySourceName = sourceName;
          const firstEntry = sourceEntries[0];
          if (firstEntry.dishId !== undefined && sourceEntries.length > 1) {
            // Multiple slots for the same dish - add portion breakdown
            const portions = dishPortionsBySlot.get(firstEntry.dishId);
            if (portions && Object.keys(portions).length > 1) {
              const portionParts: string[] = [];
              const slotKeys = Object.keys(portions).sort();
              slotKeys.forEach((slotKey) => {
                const slot = slotColumns.find((column) => column.key === slotKey);
                const venueLabel = slot?.venueLabel || slot?.dateLabel || '';
                const color = venueColorByLabel.get(venueLabel) || '#000';
                const portionQty = portions[slotKey] || 0;
                if (portionQty > 0) {
                  portionParts.push(`<span style="color:${color};font-weight:600;">${escapeHtml(venueLabel)}: ${portionQty.toFixed(0)}</span>`);
                }
              });
              if (portionParts.length > 1) {
                const totalPortions = Object.values(portions).reduce((sum: number, p: number) => sum + p, 0);
                const portionFormula = portionParts.join('+');
                displaySourceName = sourceName.replace(/\(\d+(\.\d+)?\s*порц\.\)/, `(${portionFormula}=${totalPortions.toFixed(0)} порц.)`);
              }
            }
          }

          // Apply source color to the entire displaySourceName if single venue
          if (sourceVenues.size === 1 && sourceColor !== '#000') {
            displaySourceName = `<span style="color:${sourceColor};font-weight:600;">${displaySourceName}</span>`;
          }

          // Calculate quantity breakdown for this source group
          const qtyParts: string[] = [];
          let sourceTotalQty = 0;
          const sortedVenues = Array.from(venueMap.keys()).filter(v => v).sort();
          sortedVenues.forEach((venue) => {
            const venueSourceEntries = sourceEntries.filter(e => e.venue === venue);
            const venueSourceQty = venueSourceEntries.reduce((sum, e) => sum + e.qtyTotal, 0);
            if (venueSourceQty > 0) {
              const color = venueColorByLabel.get(venue) || '#000';
              qtyParts.push(`<span style="color:${color};font-weight:600;">${escapeHtml(venue)}: ${venueSourceQty.toFixed(3)}</span>`);
              sourceTotalQty += venueSourceQty;
            }
          });
          const emptyVenueSourceEntries = sourceEntries.filter(e => !e.venue);
          const emptyVenueSourceQty = emptyVenueSourceEntries.reduce((sum, e) => sum + e.qtyTotal, 0);
          if (emptyVenueSourceQty > 0) {
            qtyParts.push(`<span style="color:#000;font-weight:600;">${emptyVenueSourceQty.toFixed(3)}</span>`);
            sourceTotalQty += emptyVenueSourceQty;
          }

          let qtyDisplay = '';
          if (qtyParts.length > 1) {
            qtyDisplay = qtyParts.join(' + ') + ` = <span style="color:#000;font-weight:600;">${sourceTotalQty.toFixed(3)}</span>`;
          } else {
            // Single venue - color the total with source color too
            qtyDisplay = `<span style="color:${sourceColor};font-weight:600;">${sourceTotalQty.toFixed(3)}</span>`;
          }

          const notesParts = Array.from(new Set(
            sourceEntries.flatMap((entry) => [
              printOperationNotes && entry.operationNotes ? `op:${entry.operationNotes.trim()}` : '',
              printCardNotes && entry.cardNotes ? `card:${entry.cardNotes.trim()}` : '',
            ]).filter((value) => value.length > 0)
          ));
          const sourcePieces = sourceEntries.reduce((sum, entry) => sum + (entry.pieces ?? 0), 0);
          if (sourceEntries.some(entry => entry.pieces != null))
            qtyDisplay += `<br/><strong>${Number(sourcePieces.toFixed(4))} шт.</strong>`;
          const notesContent = notesParts.map((value) => {
            if (value.startsWith('op:')) {
              return `<div style="color:#8e44ad;font-weight:600;margin-bottom:2px;">🔧 ${escapeHtml(value.slice(3))}</div>`;
            }
            return `<div style="color:#7f8c8d;font-style:italic;">${escapeHtml(value.slice(5))}</div>`;
          }).join('');

          return `<tr class="source-row">
            <td class="source-name">${displaySourceName}</td>
            <td class="source-qty">${qtyDisplay}</td>
            <td class="source-notes">${notesContent}</td>
          </tr>`;
        }).join('');

        // Build total quantity display with venue breakdown
        let qtyDisplay = '';
        if (hasMultipleVenues && venues.length > 0) {
          const qtyParts: string[] = [];
          venues.forEach((venue) => {
            const venueEntries = venueMap.get(venue) || [];
            const venueQty = venueEntries.reduce((sum, e) => sum + e.qtyTotal, 0);
            const color = venueColorByLabel.get(venue) || '#000';
            qtyParts.push(`<span style="color:${color};font-weight:600;">${escapeHtml(venue)}: ${venueQty.toFixed(3)}</span>`);
          });
          // Add empty venue entries if any
          const emptyVenueEntries = venueMap.get('') || [];
          if (emptyVenueEntries.length > 0) {
            const emptyQty = emptyVenueEntries.reduce((sum, e) => sum + e.qtyTotal, 0);
            qtyParts.push(`<span style="color:#000;font-weight:600;">${emptyQty.toFixed(3)}</span>`);
          }
          qtyDisplay = qtyParts.join(' + ') + ` = <span style="color:#000;font-weight:600;">${ingGroup.totalQty.toFixed(3)}</span>`;
        } else {
          qtyDisplay = `<span style="color:#000;font-weight:600;">${ingGroup.totalQty.toFixed(3)}</span>`;
        }

        const formulaStr = ingGroup.entries.map((e) => e.qtyTotal.toFixed(3)).join(' + ');
        if (ingGroup.entries.some(entry => entry.pieces != null)) {
          const pieces = ingGroup.entries.reduce((sum, entry) => sum + (entry.pieces ?? 0), 0);
          qtyDisplay += ` / <strong>${Number(pieces.toFixed(4))} шт.</strong>`;
        }
         
        return `<tr class="ingredient-row">
          <td class="ing-name" colspan="3">
            <span class="ing-label">${escapeHtml(`${ingGroup.ingredientName}${ingGroup.weightType ? ` (${ingGroup.weightType})` : ''}`)}</span>
            <span class="ing-total">Разом: ${qtyDisplay}</span>
            <span class="ing-formula">${escapeHtml(formulaStr)}</span>
          </td>
        </tr>
        ${sourcesHtml}`;
      }).join('');

      return `<section class="op-section${opIdx > 0 ? ' page-break' : ''}">
        ${venueLegendHtml}
        <div class="op-body" style="border-left: 5px solid ${opGroup.opColor}">
        <h2 class="op-title" style="background:${opGroup.opColor}; color:#fff;">${escapeHtml(opGroup.opName)}</h2>
        <div class="responsible">
          <label>Відповідальний за виконання:</label>
          <input type="text" class="responsible-input" placeholder="Ім'я та прізвище" />
        </div>
        <table class="op-table">
          <thead>
            <tr>
              <th class="source-name">Джерело (страва / н/ф)</th>
              <th class="source-qty">Кількість</th>
              <th class="source-notes">Нотатка / Операція</th>
            </tr>
          </thead>
          <tbody>${rowsHtml}</tbody>
        </table>
        </div>
      </section>`;
    }).join('');

    const html = `<!DOCTYPE html><html lang="uk"><head><meta charset="utf-8"/>
      <title>Бланк по операціях</title>
      <style>
        body{font-family:Arial,sans-serif;padding:20px;color:#1f2937;font-size:13px;}
        body,*{print-color-adjust:exact;-webkit-print-color-adjust:exact;}
        h1{margin:0 0 6px;}
        .meta{font-size:12px;color:#4b5563;margin-bottom:4px;}
        .legend{margin:14px 0;padding:10px;background:#f3f4f6;border-left:4px solid #1f2937;border-radius:3px;}
        .legend-title{font-weight:700;margin-bottom:8px;font-size:12px;}
        .legend-item{display:inline-block;margin-right:16px;font-size:11px;margin-bottom:4px;}
        .legend-color{display:inline-block;width:12px;height:12px;margin-right:4px;vertical-align:middle;border-radius:2px;}
        .op-section{margin-top:20px;break-inside:avoid;page-break-inside:avoid;}
        .op-section.page-break{break-before:page;page-break-before:always;}
        .op-section .legend{margin:0 0 10px;}
        .op-body{border-radius:4px;overflow:hidden;}
        .responsible{display:flex;align-items:center;gap:8px;padding:8px 14px;border:1px solid #d1d5db;border-top:none;font-size:13px;font-weight:700;}
        .responsible label{white-space:nowrap;}
        .responsible-input{flex:1;min-width:0;border:none;border-bottom:1px solid #1f2937;padding:3px 4px;font:inherit;font-weight:600;background:transparent;color:#111827;outline:none;}
        .responsible-input:focus{background:#fffbeb;}
        .screen-hint{margin:10px 0;padding:8px 10px;background:#fffbeb;border:1px solid #fcd34d;border-radius:4px;font-size:12px;}
        .screen-hint button{margin-left:10px;padding:4px 12px;cursor:pointer;}
        @media print{.screen-hint{display:none;}.responsible-input::placeholder{color:transparent;}}
        .op-title{margin:0;padding:8px 14px;font-size:16px;font-weight:700;letter-spacing:.3px;}
        .op-table{width:100%;border-collapse:collapse;margin:0;}
        .op-table th,.op-table td{border:1px solid #d1d5db;padding:5px 8px;vertical-align:top;}
        .op-table th{background:#f3f4f6;font-weight:700;font-size:12px;}
        .ingredient-row td{background:#e8f0fe;padding:6px 8px;}
        .ing-label{font-weight:700;font-size:14px;display:inline;}
        .ing-total{margin-left:14px;font-size:13px;color:#1a56db;display:inline;}
        .ing-formula{margin-left:8px;font-size:11px;color:#6b7280;display:inline;}
        .source-row td{background:#fff;}
        .source-row:nth-child(even) td{background:#f9fafb;}
        .source-name{width:50%;}
        .source-qty{width:20%;text-align:right;font-weight:600;}
        .source-notes{width:30%;font-style:italic;color:#6b7280;}
      </style></head><body>
      <h1>Бланк по операціях підготовки</h1>
      <div class="meta">Замовлень вибрано: ${selectedOrders.length}</div>
      <div class="meta">Сформовано: ${new Date().toLocaleString('uk-UA')}</div>
      <div class="screen-hint">Впишіть відповідальних у поля під назвами операцій, потім натисніть «Друк».
        <button type="button" onclick="window.print()">Друк</button></div>
      ${blocksHtml}
      </body></html>`;

    void openAndPrintHtml(html, {
      autoPrint: false,
      windowFeatures: 'width=1100,height=800',
      minimumDelayMs: 300,
      onReady: (printWindow) => {
        printWindow.focus();
        printWindow.document.querySelector<HTMLInputElement>('.responsible-input')?.focus();
      },
    });
  };

  const inputStyle: React.CSSProperties = {
    border: '1px solid var(--crm-border)',
    borderRadius: 'var(--crm-radius-sm)',
    padding: '8px 10px',
    width: '100%',
    boxSizing: 'border-box',
    background: 'var(--crm-input-bg)',
    color: 'var(--crm-text)',
  };
  const thStyle: React.CSSProperties = {
    padding: '10px 10px',
    background: 'var(--crm-surface-alt)',
    color: 'var(--crm-text)',
    textAlign: 'left',
    fontWeight: 700,
    fontSize: '13px',
    whiteSpace: 'nowrap',
    borderBottom: '1px solid var(--crm-border)',
  };
  const tdStyle: React.CSSProperties = {
    padding: '8px 9px',
    fontSize: '13px',
    borderBottom: '1px solid var(--crm-table-row-border)',
    verticalAlign: 'top',
    color: 'var(--crm-text)',
  };
  const panelStyle: React.CSSProperties = {
    background: 'var(--crm-surface)',
    border: '1px solid var(--crm-border)',
    padding: '16px',
    borderRadius: 'var(--crm-radius-md)',
    boxShadow: 'var(--crm-shadow-sm)',
  };
  const actionBtnBase: React.CSSProperties = {
    color: 'var(--crm-primary-contrast)',
    background: 'var(--crm-primary)',
    border: '1px solid var(--crm-primary)',
    borderRadius: 'var(--crm-radius-sm)',
    padding: '9px 14px',
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: '13px',
    lineHeight: 1.2,
  };
  const aggregateDishSummaryMarkup = aggregateDishRowsByCategory.length > 0 ? (
    <div style={{ marginTop: '14px', padding: '14px', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', background: 'var(--crm-surface)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
        <strong>Зведений список страв</strong>
        <span style={{ fontSize: '12px', color: 'var(--crm-text-muted)' }}>
          Загальна кількість: <strong>{aggregateTotalPortions.toFixed(2)}</strong>
        </span>
      </div>
      <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', fontSize: '12px' }}>
        <strong style={{ marginRight: '4px' }}>Кольори місць проведення:</strong>
        {Array.from(aggregateVenueColors.entries()).map(([venue, color]) => (
          <label key={venue} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 8px', borderRadius: '999px', background: 'var(--crm-surface-alt)', border: '1px solid var(--crm-border)' }}>
            <input
              type="color"
              aria-label={`Колір місця проведення ${venue}`}
              value={color}
              onChange={(event) => setVenueColor(venue, event.target.value)}
              style={{ width: 24, height: 24, padding: 0, border: 0, borderRadius: '50%', background: 'transparent', cursor: 'pointer' }}
            />
            {venue}
            {customVenueColors[venue] && (
              <button type="button" onClick={() => resetVenueColor(venue)} title="Відновити стандартний колір" aria-label={`Відновити стандартний колір ${venue}`} style={{ border: 0, background: 'transparent', color: 'var(--crm-text-muted)', cursor: 'pointer', fontSize: 14, lineHeight: 1 }}>↺</button>
            )}
          </label>
        ))}
      </div>
      <div style={{ marginTop: '10px', display: 'grid', gap: '12px' }}>
        {aggregateDishRowsByCategory.map(({ categoryName, rows }) => (
          <div key={categoryName} style={{ border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', overflow: 'hidden' }}>
            <div style={{ padding: '8px 10px', background: 'var(--crm-surface-alt)', fontWeight: 700 }}>
              {categoryName}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, width: '58%' }}>Страва</th>
                  <th style={{ ...thStyle, textAlign: 'right', width: '140px' }}>Порцій</th>
                  <th style={thStyle}>По місцях проведення</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.dishId}>
                    <td style={{ ...tdStyle, width: '58%', wordBreak: 'break-word' }}>{row.dishName}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, width: '140px' }}>{row.totalPortions.toFixed(2)}</td>
                    <td style={{ ...tdStyle, width: 'calc(42% - 140px)' }}>
                      {Array.from(aggregateVenueColors.entries()).map(([venue, color]) => {
                        const qty = slotColumns
                          .filter((column) => column.venueLabel === venue)
                          .reduce((sum, column) => sum + (row.portionsByVenue[column.key] || 0), 0);
                        if (qty <= 0) return null;
                        return (
                          <span key={venue} style={{ color, fontWeight: 700, marginRight: '10px', display: 'inline-block' }}>
                            {qty.toFixed(2)}
                          </span>
                        );
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  ) : null;
  const aggregateSummaryBody = aggregateSummaryCollapsed ? null : aggregateDishSummaryMarkup;

  return (
    <div className="preparation-worksheets-page" style={{ padding: '24px', maxWidth: '1450px', margin: '0 auto', color: 'var(--crm-text)' }}>
      <h2 style={{ color: 'var(--crm-text)', borderBottom: '2px solid var(--crm-primary)', paddingBottom: '8px', marginBottom: '14px' }}>Бланки підготовки до виконання робіт</h2>

      <div style={{ ...panelStyle, marginBottom: '14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <strong>Оберіть замовлення (можна декілька):</strong>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button onClick={selectAllOrders} style={{ ...actionBtnBase, background: 'var(--crm-info-bg)', color: 'var(--crm-info)', borderColor: 'var(--crm-info)' }}>
              Вибрати всі
            </button>
            <button onClick={clearAllOrdersSelection} style={{ ...actionBtnBase, background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', borderColor: 'var(--crm-border)' }}>
              Зняти вибір всіх
            </button>
            <button onClick={generateWorksheet} style={{ ...actionBtnBase, background: 'var(--crm-success-bg)', color: 'var(--crm-success)', borderColor: 'var(--crm-success)', padding: '9px 16px' }}>
              Сформувати бланк
            </button>
            <button onClick={printAggregateDishSummary} style={{ ...actionBtnBase, padding: '9px 16px' }}>
              Друк списку страв
            </button>
          </div>
        </div>
        <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--crm-text-muted)' }}>
          Обрано замовлень: <strong>{selectedOrderIds.length}</strong>
        </div>
        <div style={{ marginTop: '4px', fontSize: '12px', color: 'var(--crm-success)' }}>
          Для підготовки доступні лише замовлення зі статусом <strong>«Підтверджене»</strong>.
        </div>
        <div style={{ marginTop: '8px' }}>
          <button
            type="button"
            onClick={() => setAggregateSummaryCollapsed((value) => !value)}
            style={{ ...actionBtnBase, background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', borderColor: 'var(--crm-border)', padding: '8px 12px', fontSize: '12px' }}
          >
            {aggregateSummaryCollapsed ? 'Показати зведений список страв' : 'Згорнути зведений список страв'}
          </button>
        </div>
        {aggregateSummaryBody}
        <div style={{ maxHeight: '260px', overflowY: 'auto', marginTop: '10px', border: '1px solid var(--crm-border)', borderRadius: '8px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Вибір</th>
                <th style={thStyle}>#</th>
                <th style={thStyle}>Дата</th>
                <th style={thStyle}>Місце проведення</th>
                <th style={thStyle}>Замовник</th>
                <th style={thStyle}>Захід</th>
                <th style={thStyle}>Статус</th>
              </tr>
            </thead>
            <tbody>
              {confirmedOrders.map((o) => {
                const isChecked = selectedOrderIds.includes(o.id);
                return (
                <tr key={o.id}>
                  <td style={tdStyle}>
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleOrder(o.id)}
                    />
                  </td>
                  <td style={tdStyle}>{o.id}</td>
                  <td style={tdStyle}>{new Date(o.eventDate).toLocaleDateString('uk-UA')}</td>
                  <td style={tdStyle}>{o.eventVenue || '—'}</td>
                  <td style={tdStyle}>{o.customerName || '—'}</td>
                  <td style={tdStyle}>{o.eventName || '—'}</td>
                  <td style={tdStyle}>{o.orderStatus || '—'}</td>
                </tr>
              );
              })}
              {confirmedOrders.length === 0 && !loading && (
                <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center', color: 'var(--crm-text-muted)' }}>Замовлень немає</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div style={panelStyle}>
        <div style={{ border: '1px dashed var(--crm-border)', borderRadius: '8px', padding: '10px 12px', marginBottom: '12px', background: 'var(--crm-surface-alt)' }}>
          <div style={{ fontWeight: 700, marginBottom: '8px', color: 'var(--crm-text)' }}>Параметри шаблону друку (ширина/висота блоків)</div>
          <div style={{ marginBottom: '10px' }}>
            <div style={{ marginBottom: '6px', fontSize: '13px', color: 'var(--crm-text)', fontWeight: 600 }}>
              Підрахунок і друк по колонці
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {([
                ['brutto', 'Брутто'],
                ['netto', 'Нетто'],
                ['output', 'Вихід'],
              ] as const).map(([value, label]) => (
                <label key={value} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--crm-text)', fontWeight: 600 }}>
                  <input
                    type="radio"
                    name="worksheetQuantityMode"
                    checked={worksheetQuantityMode === value}
                    onChange={() => setWorksheetQuantityMode(value)}
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
          <div style={{ marginTop: '-4px', marginBottom: '10px', fontSize: '12px', color: 'var(--crm-text-muted)' }}>
            За замовчуванням використовується колонка Нетто для всіх видів розрахунку та друку на цьому аркуші.
          </div>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '10px', fontSize: '13px', color: 'var(--crm-text)', fontWeight: 600 }}>
            <input
              type="checkbox"
              checked={includeSemiDetailsInDishPrint}
              onChange={(e) => setIncludeSemiDetailsInDishPrint(e.target.checked)}
            />
            Друкувати калькуляції Н/Ф для обраних страв (опціонально)
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '8px 12px' }}>
            <label style={{ fontSize: '12px', color: 'var(--crm-text)' }}>Ширина Інгредієнт / Назва (%)
              <input
                type="text"
                inputMode="decimal"
                value={printLayoutDraft.nameColWidth}
                onChange={(e) => setPrintLayoutDraftValue('nameColWidth', e.target.value)}
                style={inputStyle}
              />
            </label>
            <label style={{ fontSize: '12px', color: 'var(--crm-text)' }}>Ширина компактних колонок (%)
              <input
                type="text"
                inputMode="decimal"
                value={printLayoutDraft.compactColWidth}
                onChange={(e) => setPrintLayoutDraftValue('compactColWidth', e.target.value)}
                style={inputStyle}
              />
            </label>
            <label style={{ fontSize: '12px', color: 'var(--crm-text)' }}>Ширина слот-колонок (%)
              <input
                type="text"
                inputMode="decimal"
                value={printLayoutDraft.slotColWidth}
                onChange={(e) => setPrintLayoutDraftValue('slotColWidth', e.target.value)}
                style={inputStyle}
              />
            </label>
            <label style={{ fontSize: '12px', color: 'var(--crm-text)' }}>Розмір шрифту (px)
              <input
                type="text"
                inputMode="decimal"
                value={printLayoutDraft.cellFontSize}
                onChange={(e) => setPrintLayoutDraftValue('cellFontSize', e.target.value)}
                style={inputStyle}
              />
            </label>
            <label style={{ fontSize: '12px', color: 'var(--crm-text)' }}>Висота комірки (padding Y, px)
              <input
                type="text"
                inputMode="decimal"
                value={printLayoutDraft.cellPaddingY}
                onChange={(e) => setPrintLayoutDraftValue('cellPaddingY', e.target.value)}
                style={inputStyle}
              />
            </label>
            <label style={{ fontSize: '12px', color: 'var(--crm-text)' }}>Відступ X (padding X, px)
              <input
                type="text"
                inputMode="decimal"
                value={printLayoutDraft.cellPaddingX}
                onChange={(e) => setPrintLayoutDraftValue('cellPaddingX', e.target.value)}
                style={inputStyle}
              />
            </label>
            <label style={{ fontSize: '12px', color: 'var(--crm-text)' }}>Висота рядка заголовка (line-height)
              <input
                type="text"
                inputMode="decimal"
                value={printLayoutDraft.headerLineHeight}
                onChange={(e) => setPrintLayoutDraftValue('headerLineHeight', e.target.value)}
                style={inputStyle}
              />
            </label>
          </div>
          <div style={{ marginTop: '10px' }}>
            <button
              type="button"
              onClick={savePrintLayoutPreset}
              style={{ ...actionBtnBase, background: 'var(--crm-success-bg)', color: 'var(--crm-success)', borderColor: 'var(--crm-success)', padding: '8px 14px', fontSize: '12px' }}
            >
              Зберегти параметри
            </button>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
          <button
            onClick={() => setActiveTab('dishes')}
            style={{ ...actionBtnBase, background: activeTab === 'dishes' ? 'var(--crm-primary)' : 'var(--crm-surface-alt)', color: activeTab === 'dishes' ? 'var(--crm-primary-contrast)' : 'var(--crm-text)', borderColor: activeTab === 'dishes' ? 'var(--crm-primary)' : 'var(--crm-border)' }}
          >
            Страви
          </button>
          <button
            onClick={() => setActiveTab('semi')}
            style={{ ...actionBtnBase, background: activeTab === 'semi' ? 'var(--crm-primary)' : 'var(--crm-surface-alt)', color: activeTab === 'semi' ? 'var(--crm-primary-contrast)' : 'var(--crm-text)', borderColor: activeTab === 'semi' ? 'var(--crm-primary)' : 'var(--crm-border)' }}
          >
            Напівфабрикати
          </button>
        </div>

        <div className="preparation-worksheets-filters" style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '12px' }}>
          <label style={{ minWidth: '220px', color: 'var(--crm-text)', fontSize: '13px' }}>Категорія
            <SearchSelect
              items={categorySearchItems}
              value={categoryFilter}
              onChange={setCategoryFilter}
              placeholder="🔍 Усі категорії..."
              minWidth={220}
            />
          </label>
          <label style={{ minWidth: '260px', color: 'var(--crm-text)', fontSize: '13px' }}>Страва
            <SearchSelect
              items={dishSearchItems}
              value={dishFilter}
              onChange={setDishFilter}
              placeholder="🔍 Усі страви..."
              minWidth={260}
            />
          </label>
          <label style={{ minWidth: '280px', color: 'var(--crm-text)', fontSize: '13px' }}>Пошук страви
            <input
              value={dishSearch}
              onChange={(e) => setDishSearch(e.target.value)}
              placeholder="Почніть вводити назву..."
              style={inputStyle}
            />
          </label>
          <label style={{ minWidth: '220px', color: 'var(--crm-text)', fontSize: '13px' }}>Сортування
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value as typeof sortBy)} style={inputStyle}>
              <option value="dish-asc">Страва А-Я</option>
              <option value="dish-desc">Страва Я-А</option>
              <option value="category-asc">Категорія А-Я</option>
              <option value="category-desc">Категорія Я-А</option>
              <option value="portions-asc">Порції ↑</option>
              <option value="portions-desc">Порції ↓</option>
            </select>
          </label>
          {activeTab === 'dishes' ? (
            <>
              <button onClick={exportExcel} style={{ ...actionBtnBase, background: 'var(--crm-info-bg)', color: 'var(--crm-info)', borderColor: 'var(--crm-info)' }}>Експорт Excel</button>
              <button onClick={exportPdf} style={actionBtnBase}>Друк страв (PDF)</button>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--crm-text)', fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={printOperationNotes}
                    onChange={(e) => setPrintOperationNotes(e.target.checked)}
                  />
                  🔧 Інфо операції
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--crm-text)', fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={printCardNotes}
                    onChange={(e) => setPrintCardNotes(e.target.checked)}
                  />
                  📝 Нотатки картки
                </label>
                <button onClick={printByOperations} style={{ ...actionBtnBase, background: 'var(--crm-warning-bg)', color: 'var(--crm-warning)', borderColor: 'var(--crm-warning)' }}>🔧 Друк по операціях</button>
                <Link to="/management/kitchen-execution" style={{ ...actionBtnBase, background: 'var(--crm-info-bg)', color: 'var(--crm-info)', borderColor: 'var(--crm-info)', textDecoration: 'none', display: 'inline-block' }}>Виконання роботи по операціях</Link>
              </div>
              <button
                onClick={() => setFilteredDishPrintSelection(true)}
                disabled={totalPrintableDishes === 0}
                style={{ ...actionBtnBase, background: 'var(--crm-success-bg)', color: 'var(--crm-success)', borderColor: 'var(--crm-success)', opacity: totalPrintableDishes === 0 ? 0.6 : 1, cursor: totalPrintableDishes === 0 ? 'not-allowed' : 'pointer' }}
              >
                Обрати всі блоки
              </button>
              <button
                onClick={() => setFilteredDishPrintSelection(false)}
                disabled={totalPrintableDishes === 0}
                style={{ ...actionBtnBase, background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', borderColor: 'var(--crm-border)', opacity: totalPrintableDishes === 0 ? 0.6 : 1, cursor: totalPrintableDishes === 0 ? 'not-allowed' : 'pointer' }}
              >
                Зняти всі блоки
              </button>
            </>
          ) : (
            <>
              <button onClick={exportSemiListOnlyPdf} style={actionBtnBase}>Друк загального списку (PDF)</button>
              <button onClick={exportSemiDetailsOnlyPdf} style={{ ...actionBtnBase, background: 'var(--crm-info-bg)', color: 'var(--crm-info)', borderColor: 'var(--crm-info)' }}>Друк деталізованих Н/Ф (PDF)</button>
            </>
          )}
        </div>

        {activeTab === 'dishes' && (
          <>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '10px' }}>
              <label style={{ minWidth: '320px', color: 'var(--crm-text)', fontSize: '13px' }}>
                Швидке керування друком позиції у всіх стравах
                <div ref={quickSemiSearchRef} style={{ position: 'relative' }}>
                  <input
                    value={quickSemiQuery}
                    onFocus={() => setQuickSemiDropdownOpen(true)}
                    onChange={(e) => {
                      const value = e.target.value;
                      setQuickSemiQuery(value);
                      const exact = availableSemiNames.find((name) => name.toLowerCase() === value.toLowerCase());
                      setQuickSemiName(exact ?? '');
                      setQuickSemiDropdownOpen(true);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && filteredQuickSemiNames.length > 0) {
                        const first = filteredQuickSemiNames[0];
                        setQuickSemiQuery(first);
                        setQuickSemiName(first);
                        setQuickSemiDropdownOpen(false);
                      }
                    }}
                    style={inputStyle}
                    placeholder="Почніть вводити назву..."
                    disabled={availableSemiNames.length === 0}
                  />
                  {quickSemiDropdownOpen && filteredQuickSemiNames.length > 0 && (
                    <div style={{ position: 'absolute', top: 'calc(100% + 2px)', left: 0, right: 0, maxHeight: '220px', overflowY: 'auto', background: 'var(--crm-surface)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', boxShadow: 'var(--crm-shadow-sm)', zIndex: 50 }}>
                      {filteredQuickSemiNames.map((name) => (
                        <button
                          key={`quick-semi-${name}`}
                          type="button"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => {
                            setQuickSemiQuery(name);
                            setQuickSemiName(name);
                            setQuickSemiDropdownOpen(false);
                          }}
                          style={{ display: 'block', width: '100%', textAlign: 'left', background: 'var(--crm-surface)', color: 'var(--crm-text)', border: 'none', borderBottom: '1px solid var(--crm-table-row-border)', padding: '8px 10px', cursor: 'pointer', fontSize: '13px' }}
                        >
                          {name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </label>
              <button
                onClick={() => setSemiNameSelectionAcrossDishes(quickSemiName, false)}
                disabled={!quickSemiName || !availableSemiNames.includes(quickSemiName)}
                style={{ ...actionBtnBase, background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', borderColor: 'var(--crm-danger)', opacity: !quickSemiName || !availableSemiNames.includes(quickSemiName) ? 0.6 : 1, cursor: !quickSemiName || !availableSemiNames.includes(quickSemiName) ? 'not-allowed' : 'pointer' }}
              >
                Вимкнути у всіх стравах
              </button>
              <button
                onClick={() => setSemiNameSelectionAcrossDishes(quickSemiName, true)}
                disabled={!quickSemiName || !availableSemiNames.includes(quickSemiName)}
                style={{ ...actionBtnBase, background: 'var(--crm-success-bg)', color: 'var(--crm-success)', borderColor: 'var(--crm-success)', opacity: !quickSemiName || !availableSemiNames.includes(quickSemiName) ? 0.6 : 1, cursor: !quickSemiName || !availableSemiNames.includes(quickSemiName) ? 'not-allowed' : 'pointer' }}
              >
                Увімкнути у всіх стравах
              </button>
            </div>

            <div style={{ marginBottom: '10px', color: 'var(--crm-text-muted)', fontSize: '13px' }}>
              Показано {filteredRows.length} страв у форматі блоків із розрахунком по датах та місцях проведення.
            </div>
            <div style={{ marginBottom: '12px', color: 'var(--crm-text)', fontSize: '13px', fontWeight: 600 }}>
              Блоків обрано для друку: {selectedPrintableDishes} / {totalPrintableDishes}. Позицій у блоках: {selectedPrintableItems} / {totalPrintableItems}
            </div>
            <div style={{ marginBottom: '12px', display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '13px', color: 'var(--crm-text)', fontWeight: 700 }}>
              <span>Загальний час приготування: {formatDurationLabel(totalPreparationTimeSeconds)}</span>
              <span>Загальний час на видачу: {formatDurationLabel(totalServeTimeSeconds)}</span>
            </div>

            {filteredRows.length > 0 && slotColumns.length > 0 && (
              <div style={{ display: 'grid', gap: '14px' }}>
                {filteredRows.map((row) => (
                  <div key={row.dishId} style={{ border: '1px solid var(--crm-border)', borderRadius: '10px', overflowX: 'auto', background: 'var(--crm-surface)' }}>
                    <div style={{ background: 'var(--crm-surface-alt)', padding: '10px 12px', borderBottom: '1px solid var(--crm-border)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 700, color: 'var(--crm-text)' }}>
                          <input
                            type="checkbox"
                            checked={isDishSelectedForPrint(row.dishId)}
                            onChange={() => togglePrintDish(row.dishId)}
                          />
                          {formatPrepDishName(row.dishId, row.dishName)}
                        </label>
                        <button
                          type="button"
                          onClick={() => printSingleDish(row)}
                          style={{ ...actionBtnBase, padding: '7px 12px', fontSize: '12px' }}
                        >
                          🖨 Друк
                        </button>
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--crm-text-muted)', marginTop: '2px' }}>Всього порцій: {row.totalPortions.toFixed(2)}</div>
                      <div style={{ fontSize: '12px', color: 'var(--crm-text-muted)', marginTop: '2px' }}>
                        Час приготування: {formatDurationLabel(calcRequiredTime(row.cookingTimePerPortion, row.totalPortions))}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--crm-text-muted)', marginTop: '2px' }}>
                        Час на видачу: {formatDurationLabel(calcRequiredTime(row.serveTimePerPortion, row.totalPortions))}
                      </div>
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={thStyle}>Друк</th>
                          <th style={thStyle}>Інгредієнт</th>
                          <th style={{ ...thStyle, textAlign: 'right' }}>Кількість на 1 порцію</th>
                          <th style={{ ...thStyle, textAlign: 'right', background: 'var(--crm-success-bg)', color: 'var(--crm-success)' }}>Загалом</th>
                          {slotColumns.map((column) => (
                            <th key={`${row.dishId}-head-${column.key}`} style={thStyle}>
                              <div>{column.dateLabel}</div>
                              <div style={{ fontSize: '11px', opacity: 0.95 }}>{column.venueLabel}</div>
                              <div style={{ fontSize: '12px', marginTop: '2px', color: 'var(--crm-text-muted)' }}>
                                Порції: {(row.portionsBySlot[column.key] || 0).toFixed(2)}
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {row.semiItems.length === 0 ? (
                          <tr>
                            <td style={tdStyle}>—</td>
                            <td style={tdStyle}>—</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>—</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>—</td>
                            {slotColumns.map((column) => (
                              <td key={`${row.dishId}-empty-${column.key}`} style={tdStyle}>—</td>
                            ))}
                          </tr>
                        ) : (
                          row.semiItems.map((semi) => {
                            const canExpandSemi = semi.id != null;
                            const isExpandedSemi = canExpandSemi ? isDishSemiExpanded(row.dishId, semi.id!) : false;
                            const inlineDetailRows = canExpandSemi && isExpandedSemi ? buildSemiDetailRows(semi.id) : [];
                            const inlineSemiSummary = canExpandSemi ? buildDishSemiSummaryForPrint(row, semi) : null;
                            return (
                              <React.Fragment key={`${row.dishId}-${semi.name}-${semi.piecesPerPortion != null ? 'pieces' : 'weight'}`}>
                                <tr
                                  onClick={canExpandSemi ? () => toggleDishSemiExpanded(row.dishId, semi.id!) : undefined}
                                  style={canExpandSemi ? { cursor: 'pointer', background: isExpandedSemi ? 'var(--crm-surface-alt)' : 'transparent' } : undefined}
                                >
                                  <td style={{ ...tdStyle, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                                    <input
                                      type="checkbox"
                                      checked={isPrintItemSelected(row.dishId, semi.name)}
                                      onChange={() => togglePrintItem(row.dishId, semi.name)}
                                    />
                                  </td>
                                  <td style={tdStyle}>
                                    {semi.name}
                                    {canExpandSemi && (
                                      <span style={{ marginLeft: '8px', fontSize: '11px', color: 'var(--crm-text-muted)' }}>
                                        {isExpandedSemi ? '▲ калькуляція Н/Ф' : '▼ калькуляція Н/Ф'}
                                      </span>
                                    )}
                                  </td>
                                  <td style={{ ...tdStyle, textAlign: 'right' }}>{formatPreparationQuantity(semi.qtyPerPortion, semi.piecesPerPortion)}</td>
                                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: 'var(--crm-success)' }}>{formatPreparationQuantity(semi.qtyPerPortion, semi.piecesPerPortion, row.totalPortions)}</td>
                                  {slotColumns.map((column) => {
                                    const portions = row.portionsBySlot[column.key] || 0;
                                    return (
                                      <td key={`${row.dishId}-${semi.name}-${column.key}`} style={tdStyle}>
                                        {portions > 0 ? <strong>{formatPreparationQuantity(semi.qtyPerPortion, semi.piecesPerPortion, portions)}</strong> : '—'}
                                      </td>
                                    );
                                  })}
                                </tr>
                                {canExpandSemi && isExpandedSemi && inlineSemiSummary && (
                                  <tr>
                                    <td colSpan={4 + slotColumns.length} style={{ ...tdStyle, padding: '10px 12px', background: 'var(--crm-surface-alt)' }}>
                                      <div style={{ border: '1px solid var(--crm-border)', borderRadius: '8px', padding: '10px', background: 'var(--crm-surface)' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                          <strong style={{ fontSize: '13px', color: 'var(--crm-text)' }}>
                                            Калькуляція Н/Ф: {semi.name}
                                          </strong>
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              printSingleSemiFromDish(row, semi);
                                            }}
                                            style={{ ...actionBtnBase, background: 'var(--crm-info-bg)', color: 'var(--crm-info)', borderColor: 'var(--crm-info)', padding: '7px 12px', fontSize: '12px' }}
                                          >
                                            🖨 Друк Н/Ф
                                          </button>
                                        </div>
                                        <div style={{ fontSize: '12px', color: 'var(--crm-text-muted)', marginBottom: '8px' }}>
                                          Всього потрібно Н/Ф: <strong>{inlineSemiSummary.totalQty.toFixed(4)}</strong>
                                        </div>
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                          <thead>
                                            <tr>
                                              <th style={thStyle}>Позиція</th>
                                              <th style={{ ...thStyle, textAlign: 'right' }}>На 1 од. н/ф</th>
                                              <th style={{ ...thStyle, textAlign: 'right', background: 'var(--crm-success-bg)', color: 'var(--crm-success)' }}>Загалом</th>
                                              {slotColumns.map((column) => (
                                                <th key={`${row.dishId}-${semi.name}-inline-head-${column.key}`} style={thStyle}>
                                                  <div>{column.dateLabel}</div>
                                                  <div style={{ fontSize: '11px', opacity: 0.95 }}>{column.venueLabel}</div>
                                                </th>
                                              ))}
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {inlineDetailRows.length === 0 ? (
                                              <tr>
                                                <td colSpan={3 + slotColumns.length} style={{ ...tdStyle, textAlign: 'center', color: 'var(--crm-text-muted)' }}>
                                                  Немає розкладки для цього Н/Ф.
                                                </td>
                                              </tr>
                                            ) : (
                                              inlineDetailRows.map((item) => (
                                                <tr key={`${row.dishId}-${semi.name}-inline-${item.rowKey}`}>
                                                  <td style={tdStyle}>{item.itemType === 'semi' ? `Н/Ф: ${item.itemName}` : item.itemName}</td>
                                                  <td style={{ ...tdStyle, textAlign: 'right' }}>{item.qtyPerSemiUnit.toFixed(4)}</td>
                                                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: 'var(--crm-success)' }}>
                                                    {(inlineSemiSummary.totalQty * item.qtyPerSemiUnit).toFixed(4)}
                                                  </td>
                                                  {slotColumns.map((column) => {
                                                    const semiQty = inlineSemiSummary.qtyBySlot[column.key] || 0;
                                                    const qty = semiQty * item.qtyPerSemiUnit;
                                                    return (
                                                      <td key={`${row.dishId}-${semi.name}-inline-${item.rowKey}-${column.key}`} style={tdStyle}>
                                                        {qty > 0 ? qty.toFixed(4) : '—'}
                                                      </td>
                                                    );
                                                  })}
                                                </tr>
                                              ))
                                            )}
                                          </tbody>
                                        </table>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'semi' && semiSummary.length > 0 && slotColumns.length > 0 && (
          <div style={{ marginTop: '6px' }}>
            <h3 style={{ margin: '0 0 10px', color: 'var(--crm-text)' }}>Напівфабрикати: загальний розрахунок</h3>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap' }}>
              <strong style={{ fontSize: '13px', color: 'var(--crm-text)' }}>Деталізація:</strong>
              <button
                onClick={() => setAllSemiDetailSelection(true)}
                style={{ ...actionBtnBase, background: 'var(--crm-success-bg)', color: 'var(--crm-success)', borderColor: 'var(--crm-success)', padding: '8px 12px' }}
              >
                Всі
              </button>
              <button
                onClick={() => setAllSemiDetailSelection(false)}
                style={{ ...actionBtnBase, background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', borderColor: 'var(--crm-border)', padding: '8px 12px' }}
              >
                Жодної
              </button>
              <span style={{ fontSize: '13px', color: 'var(--crm-text)', fontWeight: 600 }}>Обрано: {selectedSemiDetailBlocks.length}</span>
              <span style={{ margin: '0 8px', color: 'var(--crm-text-muted)' }}>|</span>
              <strong style={{ fontSize: '13px', color: 'var(--crm-text)' }}>Загальний друк:</strong>
              <button
                onClick={() => setAllSemiListSelection(true)}
                style={{ ...actionBtnBase, background: 'var(--crm-info-bg)', color: 'var(--crm-info)', borderColor: 'var(--crm-info)', padding: '8px 12px' }}
              >
                Всі
              </button>
              <button
                onClick={() => setAllSemiListSelection(false)}
                style={{ ...actionBtnBase, background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', borderColor: 'var(--crm-border)', padding: '8px 12px' }}
              >
                Жодної
              </button>
              <span style={{ fontSize: '13px', color: 'var(--crm-text)', fontWeight: 600 }}>
                Обрано: {filteredSemiSummary.filter((s) => isSemiListItemSelected(s.key)).length} / {filteredSemiSummary.length}
                {filteredSemiSummary.length !== semiSummary.length ? ` (всього: ${semiSummary.length})` : ''}
              </span>
              <label style={{ minWidth: '280px', color: 'var(--crm-text)', fontSize: '13px' }}>
                Пошук по напівфабрикату
                <input
                  value={semiSearch}
                  onChange={(e) => setSemiSearch(e.target.value)}
                  placeholder="Почніть вводити назву..."
                  style={inputStyle}
                />
              </label>
            </div>
            <div style={{ overflowX: 'auto', marginBottom: '14px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Деталізувати</th>
                    <th style={{ ...thStyle, background: 'var(--crm-info-bg)', color: 'var(--crm-info)' }}>Друк</th>
                    <th style={thStyle}>Напівфабрикат</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Всього потрібно</th>
                    {slotColumns.map((column) => (
                      <th key={`semi-summary-${column.key}`} style={thStyle}>
                        <div>{column.dateLabel}</div>
                        <div style={{ fontSize: '11px', opacity: 0.95 }}>{column.venueLabel}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredSemiSummary.map((semi) => (
                    <tr key={`semi-summary-row-${semi.key}`} style={{ background: selectedSemiKeys.includes(semi.key) ? 'var(--crm-surface-alt)' : 'transparent' }}>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={semi.semiId != null && selectedSemiKeys.includes(semi.key)}
                          disabled={semi.semiId == null}
                          onChange={() => toggleSemiDetailSelection(semi.key)}
                        />
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={isSemiListItemSelected(semi.key)}
                          onChange={() => toggleSemiListItem(semi.key)}
                        />
                      </td>
                      <td style={tdStyle}>
                        {semi.semiName}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700 }}>{semi.totalQty.toFixed(4)}</td>
                      {slotColumns.map((column) => (
                        <td key={`semi-summary-cell-${semi.key}-${column.key}`} style={tdStyle}>
                          {(semi.qtyBySlot[column.key] || 0).toFixed(4)}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {filteredSemiSummary.length === 0 && (
                    <tr>
                      <td colSpan={4 + slotColumns.length} style={{ ...tdStyle, textAlign: 'center', color: 'var(--crm-text-muted)' }}>
                        Нічого не знайдено за поточним пошуком.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {selectedSemiDetailBlocks.map(({ summary, detailRows }) => (
              <div key={`semi-detail-block-${summary.key}`} style={{ marginTop: '14px', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', overflowX: 'auto', background: 'var(--crm-surface)' }}>
                <div style={{ background: 'var(--crm-surface-alt)', padding: '10px 12px', borderBottom: '1px solid var(--crm-border)', fontWeight: 700, color: 'var(--crm-text)' }}>
                  {summary.semiName} <span style={{ fontSize: '12px', color: 'var(--crm-text-muted)', marginLeft: '8px' }}>Всього потрібно: {summary.totalQty.toFixed(4)}</span>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Тип</th>
                      <th style={thStyle}>Позиція</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>На 1 од. н/ф</th>
                      <th style={{ ...thStyle, textAlign: 'right', background: 'var(--crm-success-bg)', color: 'var(--crm-success)' }}>Загалом</th>
                      {slotColumns.map((column) => (
                        <th key={`semi-products-${summary.key}-${column.key}`} style={thStyle}>
                          <div>{column.dateLabel}</div>
                          <div style={{ fontSize: '11px', opacity: 0.95 }}>{column.venueLabel}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {detailRows.length === 0 ? (
                      <tr>
                        <td colSpan={4 + slotColumns.length} style={{ ...tdStyle, textAlign: 'center', color: 'var(--crm-text-muted)' }}>
                          Для цього напівфабрикату немає розкладки.
                        </td>
                      </tr>
                    ) : (
                      detailRows.map((item) => (
                        <tr key={`semi-detail-row-${summary.key}-${item.rowKey}`}>
                          <td style={tdStyle}>{item.itemType === 'semi' ? 'Н/Ф' : 'Продукт'}</td>
                          <td style={tdStyle}>{item.itemName}</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{item.qtyPerSemiUnit.toFixed(4)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: 'var(--crm-success)' }}>
                            {(summary.totalQty * item.qtyPerSemiUnit).toFixed(4)}
                          </td>
                          {slotColumns.map((column) => {
                            const semiQty = summary.qtyBySlot[column.key] || 0;
                            const productQty = semiQty * item.qtyPerSemiUnit;
                            return (
                              <td key={`semi-detail-cell-${summary.key}-${item.rowKey}-${column.key}`} style={tdStyle}>
                                {productQty.toFixed(4)}
                              </td>
                            );
                          })}
                        </tr>
                      ))
                    )}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={4} style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, background: 'var(--crm-info-bg)', color: 'var(--crm-info)' }}>
                        Н/Ф по днях:
                      </td>
                      {slotColumns.map((column) => (
                        <td key={`semi-products-foot-${summary.key}-${column.key}`} style={{ ...tdStyle, fontWeight: 700, background: 'var(--crm-info-bg)', color: 'var(--crm-info)' }}>
                          Н/Ф: {(summary.qtyBySlot[column.key] || 0).toFixed(4)}
                        </td>
                      ))}
                    </tr>
                  </tfoot>
                </table>
              </div>
            ))}
          </div>
        )}
        {filteredRows.length === 0 && (
          <div style={{ ...tdStyle, textAlign: 'center', color: 'var(--crm-text-muted)', padding: '24px' }}>
            Немає даних. Оберіть замовлення і натисніть "Сформувати бланк".
          </div>
        )}
      </div>
    </div>
  );
};

export default PreparationWorksheetsPage;
